import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    FiBox,
    FiBell,
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiHome,
    FiPercent,
    FiPrinter,
    FiSettings,
    FiStar,
    FiTag,
    FiUser,
    FiUsers,
} from 'react-icons/fi';
import { TiPin } from 'react-icons/ti';
import { RiStore2Line } from 'react-icons/ri';
import {
    BARCODE_PREFIX_MAX_LENGTH,
    BARCODE_MODE_OPTIONS,
    DEFAULT_BARCODE_PREFIX_MAPPINGS,
    getBarcodePrefixMappings,
    isReservedBarcodePrefix,
    saveBarcodePrefixMappings,
    sanitizeBarcodePrefixInput,
} from '../utils/barcodeSettings';
import {
    DEFAULT_PRINT_MODE,
    PRINT_MODE_DESCRIPTIONS,
    PRINT_MODE_LABELS,
    PRINT_MODE_OPTIONS,
    getPrintMode,
    savePrintMode,
} from '../utils/printSettings';
import {
    createBranch,
    createCashRegister,
    deleteBranch,
    getBarcodePricingRule,
    getBranchRequestConfig,
    getCategories,
    getCategoryBarcodes,
    getPrintReceiptRedirectDefaults,
    getPosSoldOutDisplaySettings,
    getPriceRecommendationSettings,
    getPurchaseOrderNotificationSettings,
    getPurchaseOrderReasonsSettings,
    regenerateCategoryBarcodes,
    updateBarcodePricingRule,
    updateBranchRequestConfig,
    updatePosSoldOutDisplaySettings,
    updatePriceRecommendationSettings,
    updatePurchaseOrderNotificationSettings,
    updatePurchaseOrderReasonsSettings,
    getBranches,
    getUsers,
    updateBranch,
    getTaxes,
    createTax,
    updateTax,
    getBranchTaxes,
    updateBranchTaxes,
    getClientPricingCategories,
    createClientPricingCategory,
    updateClientPricingCategory,
    getClientPricingDiscountCatalog,
    updateClientPricingDiscount,
    bulkUpdateClientPricingDiscounts,
    restoreBranch,
    getProducts,
    getPromotions,
    getRoles,
    sendTestWebPushNotification,
    createPromotion,
    updatePromotion,
    deletePromotion,
    updatePrintReceiptRedirectDefaults,
} from '../api';
import BranchUsersPage from './BranchUsersPage';
import CashRegistersPage from './CashRegistersPage';
import ProductList from '../components/ProductList';
import PosSlotSettingsPanel from '../components/PosSlotSettingsPanel';
import DteSettingsPanel from '../components/DteSettingsPanel';
import AdminPriceListPage from './AdminPriceListPage';
import CategoriesAdminPage from './CategoriesAdminPage';
import useCurrentUser from '../hooks/useCurrentUser';
import useMyPreferences from '../hooks/useMyPreferences';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { hasAnyPermission, hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import HorizontalPillTabs from '../components/HorizontalPillTabs';
import {
    DEFAULT_PRINT_RECEIPT_REDIRECT,
    DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS,
    DEFAULT_PURCHASE_ORDER_REVIEW,
    normalizePurchaseOrderReview,
    normalizePrintReceiptRedirectDefaults,
    normalizePrintReceiptRedirect,
} from '../utils/userPreferences';
import {
    POS_SOLD_OUT_DISPLAY_OPTIONS,
    POS_SOLD_OUT_MODE_NORMAL,
    normalizePosSoldOutMode,
} from '../utils/posSoldOutDisplay';
import {
    disableBrowserPushNotifications,
    enableBrowserPushNotifications,
    loadBrowserPushState,
} from '../utils/webPushNotifications';

const MODE_LABELS = {
    weight: 'Peso',
    amount: 'Cantidad',
    price: 'Precio',
};

const CLIENT_TYPE_LABELS = {
    detalle: 'Detalle',
    mayorista: 'Mayorista',
    importador: 'Importador',
};

const PRICE_SYNC_TYPE_LABELS = {
    percent: 'Porcentaje',
    fixed: 'Monto fijo',
};

const PROMOTION_TYPE_LABELS = {
    temporary_price: 'Precio temporal',
    pack: 'Pack mismo producto',
};

const DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES = ['AdminSucursal', 'Vendedor'];

const normalizeRoleNameList = (value, fallback = DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES) => {
    const source = Array.isArray(value) ? value : fallback;
    return Array.from(new Set(source.map((entry) => String(entry || '').trim()).filter(Boolean)));
};

const buildPurchaseOrderNotificationRoleOptions = (roles, selectedRoleNames) => {
    const selectedNames = normalizeRoleNameList(selectedRoleNames, DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES);
    const optionsByName = new Map();
    selectedNames.forEach((name) => {
        optionsByName.set(name, {
            id: `configured-${name}`,
            name,
            description: '',
            configuredOnly: true,
        });
    });
    (Array.isArray(roles) ? roles : []).forEach((role) => {
        const name = String(role?.name || '').trim();
        if (!name) return;
        const permissions = role.Permissions || role.permissions || [];
        const canReadPurchaseOrders = permissions.some((permission) => permission?.key === 'purchase_order.read');
        if (!canReadPurchaseOrders && !optionsByName.has(name)) return;
        optionsByName.set(name, {
            id: role.id || name,
            name,
            description: role.description || '',
            configuredOnly: !canReadPurchaseOrders,
        });
    });
    return Array.from(optionsByName.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const getPurchaseOrderTestNotificationMessage = (reason) => {
    if (reason === 'no-subscriptions') {
        return 'Este usuario no tiene navegadores suscritos. Activa este navegador primero.';
    }
    if (reason === 'web-push-not-configured') {
        return 'El servidor no tiene claves VAPID configuradas.';
    }
    return 'No se pudo enviar la notificacion de prueba.';
};

const formatCategoryBarcodeDate = (value) => {
    if (!value) return 'Sin fecha';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Sin fecha' : date.toLocaleString('es-CL');
};

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const emptyPromotionForm = () => ({
    name: '',
    type: 'temporary_price',
    status: 'active',
    startsAt: '',
    endsAt: '',
    appliesToSales: true,
    appliesToQuotations: true,
    varietyPriceIds: [],
    promoUnitPrice: '',
    packQuantity: '',
    packPrice: '',
});

const emptyPriceRecommendationSettings = () => ({
    autoUpdatePrices: true,
    rounding: {
        mode: 'hundreds_threshold',
        threshold: '20',
    },
});

const buildRegisterTemplate = (index) => ({
    name: `Caja ${index + 1}`,
    isActive: true,
    allowedClientTypes: [],
    discountApproverUserId: '',
    maxOpenSales: '5',
    users: [],
});

const PURCHASE_ORDER_REASON_GROUPS = [
    { id: 'reduce', label: 'Reducir cantidad' },
    { id: 'remove', label: 'Eliminar item' },
    { id: 'increase', label: 'Aumentar cantidad' },
];

const SETTINGS_TOPICS = [
    {
        id: 'personal',
        label: 'Personal',
        description: 'Preferencias propias.',
        sections: [
            {
                id: 'registers-printing',
                label: 'Impresion',
                description: 'Recibos y redireccion.',
                branchScoped: false,
            },
            {
                id: 'purchase-order-review',
                label: 'Revision de ordenes',
                description: 'Guardado y confirmacion.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'branches',
        label: 'Sucursales',
        description: 'Configuracion y estructura.',
        sections: [
            {
                id: 'branch-directory',
                label: 'Listado de sucursales',
                description: 'Crear, editar y eliminar.',
                branchScoped: false,
            },
            {
                id: 'branch-price-sync',
                label: 'Sincronizacion de precios',
                description: 'Casa matriz y ajustes.',
                branchScoped: true,
            },
            {
                id: 'purchase-order-reasons',
                label: 'Motivos de ordenes',
                description: 'Ajustes de ordenes de clientes.',
                branchScoped: false,
            },
            {
                id: 'purchase-order-notifications',
                label: 'Notificaciones de ordenes',
                description: 'Avisos push para nuevas ordenes.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'catalog',
        label: 'Catalogo',
        description: 'Productos y categorias.',
        sections: [
            {
                id: 'catalog-categories',
                label: 'Categorias',
                description: 'Gestion de categorias.',
                branchScoped: true,
            },
            {
                id: 'catalog-price-list',
                label: 'Lista de precios',
                description: 'Editar precios y disponibilidad.',
                branchScoped: true,
            },
            {
                id: 'catalog-promotions',
                label: 'Promociones',
                description: 'Precios temporales y packs.',
                branchScoped: true,
            },
            {
                id: 'catalog-pos-sales',
                label: 'Venta POS',
                description: 'Visualizacion en caja.',
                branchScoped: false,
            },
            {
                id: 'catalog-price-recommendations',
                label: 'Recomendaciones',
                description: 'Reglas desde compras.',
                branchScoped: true,
            },
            {
                id: 'catalog-products',
                label: 'Productos',
                description: 'Lista y mantenimiento.',
                branchScoped: true,
            },
        ],
    },
    {
        id: 'clients',
        label: 'Clientes',
        description: 'Categorias y precios.',
        sections: [
            {
                id: 'client-pricing-categories',
                label: 'Categorias de descuento',
                description: 'Descuentos por precio mayorista.',
                branchScoped: true,
            },
        ],
    },
    {
        id: 'taxes',
        label: 'Impuestos',
        description: 'IVA y configuracion fiscal.',
        sections: [
            {
                id: 'taxes-manage',
                label: 'Impuestos',
                description: 'Configura impuestos y activacion por sucursal.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'dte',
        label: 'DTE',
        description: 'Emisor, folios y certificados.',
        sections: [
            {
                id: 'dte-company',
                label: 'Perfil emisor',
                description: 'RUT, giro y resolucion.',
                branchScoped: false,
            },
            {
                id: 'dte-credentials',
                label: 'Credenciales',
                description: 'API key y certificado.',
                branchScoped: false,
            },
            {
                id: 'dte-branches',
                label: 'Sucursales fiscales',
                description: 'Origen fiscal por sucursal.',
                branchScoped: false,
            },
            {
                id: 'dte-cafs',
                label: 'Folios CAF',
                description: 'Rangos autorizados.',
                branchScoped: false,
            },
            {
                id: 'dte-automation',
                label: 'Automatizacion',
                description: 'Manual o automatico.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'users',
        label: 'Usuarios',
        description: 'Acceso general y roles.',
        sections: [
            {
                id: 'users-access',
                label: 'Usuarios',
                description: 'Gestiona accesos y roles.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'registers',
        label: 'Cajas',
        description: 'Cajas, usuarios y codigos de barras.',
        sections: [
            {
                id: 'registers-cash',
                label: 'Administrar cajas',
                description: 'Crear y asignar usuarios.',
                branchScoped: true,
            },
            {
                id: 'registers-print-defaults',
                label: 'Impresion global',
                description: 'Redireccion por defecto.',
                branchScoped: false,
            },
            {
                id: 'registers-barcode',
                label: 'Codigos de barras',
                description: 'Prefijos y modo de lectura.',
                branchScoped: false,
            },
            {
                id: 'registers-pos-favs',
                label: 'Favs POS',
                description: 'Atajos por caja.',
                branchScoped: true,
            },
        ],
    },
];

const SettingsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { branchId: routeBranchId } = useParams();
    // Settings can edit another branch without changing the app-wide selectedBranchId.
    const [activeBranchId, setActiveBranchId] = useState(
        routeBranchId || localStorage.getItem('selectedBranchId') || ''
    );
    const [activeTopic, setActiveTopic] = useState('branches');
    const [activeSection, setActiveSection] = useState('branch-directory');
    const [mappings, setMappings] = useState([]);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [barcodePricingRule, setBarcodePricingRule] = useState({
        mode: 'quantity',
        thresholds: {
            detalle: '',
            mayorista: '',
            importador: '',
        },
    });
    const [barcodeRuleLoading, setBarcodeRuleLoading] = useState(false);
    const [barcodeRuleError, setBarcodeRuleError] = useState('');
    const [barcodeRuleSaved, setBarcodeRuleSaved] = useState(false);
    const [categoryBarcodes, setCategoryBarcodes] = useState([]);
    const [categoryBarcodeLoading, setCategoryBarcodeLoading] = useState(false);
    const [categoryBarcodeRegenerating, setCategoryBarcodeRegenerating] = useState(false);
    const [categoryBarcodeError, setCategoryBarcodeError] = useState('');
    const [categoryBarcodeSaved, setCategoryBarcodeSaved] = useState(false);
    const [categoryBarcodeInactiveCount, setCategoryBarcodeInactiveCount] = useState(0);
    const [branches, setBranches] = useState([]);
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchError, setBranchError] = useState('');
    const [branchPage, setBranchPage] = useState(1);
    const [showDeletedBranches, setShowDeletedBranches] = useState(false);
    const [editingBranchId, setEditingBranchId] = useState(null);
    const [editedBranchName, setEditedBranchName] = useState('');
    const [newBranchName, setNewBranchName] = useState('');
    const [branchCreateOpen, setBranchCreateOpen] = useState(false);
    const [branchCreating, setBranchCreating] = useState(false);
    const [branchCreateError, setBranchCreateError] = useState('');
    const [branchCreateNotice, setBranchCreateNotice] = useState('');
    const [newBranchRegisters, setNewBranchRegisters] = useState(() => [buildRegisterTemplate(0)]);
    const [branchCreateUsers, setBranchCreateUsers] = useState([]);
    const [branchCreateUsersLoading, setBranchCreateUsersLoading] = useState(false);
    const [branchCreateUsersError, setBranchCreateUsersError] = useState('');
    const [priceSync, setPriceSync] = useState({
        sourceBranchId: '',
        adjustmentType: '',
        adjustmentValue: '',
    });
    const [branchRequestConfig, setBranchRequestConfig] = useState({
        mainBranchId: '',
        resolvedMainBranchId: null,
        defaultQuotationCategoryIds: [],
        defaultPurchaseOrderCategoryIds: [],
    });
    const [branchRequestConfigLoading, setBranchRequestConfigLoading] = useState(false);
    const [branchRequestConfigError, setBranchRequestConfigError] = useState('');
    const [branchRequestConfigSaved, setBranchRequestConfigSaved] = useState(false);
    const [purchaseOrderReasons, setPurchaseOrderReasons] = useState({
        reduce: [],
        remove: [],
        increase: [],
    });
    const [purchaseOrderReasonsLoading, setPurchaseOrderReasonsLoading] = useState(false);
    const [purchaseOrderReasonsSaving, setPurchaseOrderReasonsSaving] = useState(false);
    const [purchaseOrderReasonsError, setPurchaseOrderReasonsError] = useState('');
    const [purchaseOrderReasonsSaved, setPurchaseOrderReasonsSaved] = useState(false);
    const [purchaseOrderNotificationSettings, setPurchaseOrderNotificationSettings] = useState({
        enabled: true,
        recipientRoleNames: DEFAULT_PURCHASE_ORDER_NOTIFICATION_ROLE_NAMES,
        respectBranchAssignments: true,
        includeGlobalAdminUsers: true,
    });
    const [purchaseOrderNotificationRoles, setPurchaseOrderNotificationRoles] = useState([]);
    const [purchaseOrderNotificationsLoading, setPurchaseOrderNotificationsLoading] = useState(false);
    const [purchaseOrderNotificationsSaving, setPurchaseOrderNotificationsSaving] = useState(false);
    const [purchaseOrderNotificationsError, setPurchaseOrderNotificationsError] = useState('');
    const [purchaseOrderNotificationsRoleWarning, setPurchaseOrderNotificationsRoleWarning] = useState('');
    const [purchaseOrderNotificationsSaved, setPurchaseOrderNotificationsSaved] = useState(false);
    const [purchaseOrderBrowserPushState, setPurchaseOrderBrowserPushState] = useState({
        loading: false,
        saving: false,
        supported: true,
        configured: false,
        permission: 'default',
        serverSubscription: null,
        error: '',
        message: '',
        testing: false,
    });
    const [purchaseOrderReview, setPurchaseOrderReview] = useState(DEFAULT_PURCHASE_ORDER_REVIEW);
    const [purchaseOrderReviewSaving, setPurchaseOrderReviewSaving] = useState(false);
    const [purchaseOrderReviewError, setPurchaseOrderReviewError] = useState('');
    const [purchaseOrderReviewSaved, setPurchaseOrderReviewSaved] = useState(false);
    const [clientPricingCategories, setClientPricingCategories] = useState([]);
    const [clientPricingCategoriesLoading, setClientPricingCategoriesLoading] = useState(false);
    const [clientPricingCategoriesError, setClientPricingCategoriesError] = useState('');
    const [clientPricingCategoryForm, setClientPricingCategoryForm] = useState({
        name: '',
        description: '',
        isActive: true,
    });
    const [clientPricingEditingId, setClientPricingEditingId] = useState(null);
    const [selectedClientPricingCategoryId, setSelectedClientPricingCategoryId] = useState('');
    const [clientPricingDiscountRows, setClientPricingDiscountRows] = useState([]);
    const [clientPricingDiscountsLoading, setClientPricingDiscountsLoading] = useState(false);
    const [clientPricingDiscountsError, setClientPricingDiscountsError] = useState('');
    const [clientPricingDiscountsSaved, setClientPricingDiscountsSaved] = useState(false);
    const [clientPricingSearch, setClientPricingSearch] = useState('');
    const [clientPricingBulkForm, setClientPricingBulkForm] = useState({
        discountType: 'amount',
        discountValue: '',
    });
    const [clientPricingBulkSaving, setClientPricingBulkSaving] = useState(false);
    const [clientPricingRowSaving, setClientPricingRowSaving] = useState({});
    const [clientPricingRowSaveStatus, setClientPricingRowSaveStatus] = useState({});
    const [quotationCategoryOptions, setQuotationCategoryOptions] = useState([]);
    const [quotationCategoryLoading, setQuotationCategoryLoading] = useState(false);
    const [quotationCategoryError, setQuotationCategoryError] = useState('');
    const [priceSyncError, setPriceSyncError] = useState('');
    const [priceSyncSaved, setPriceSyncSaved] = useState(false);
    const [priceSyncLoading, setPriceSyncLoading] = useState(false);
    const [printMode, setPrintMode] = useState(DEFAULT_PRINT_MODE);
    const [printModeSaved, setPrintModeSaved] = useState(false);
    const [printReceiptRedirect, setPrintReceiptRedirect] = useState(DEFAULT_PRINT_RECEIPT_REDIRECT);
    const [printRedirectDefaults, setPrintRedirectDefaults] = useState(DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS);
    const [printRedirectDefaultsLoading, setPrintRedirectDefaultsLoading] = useState(false);
    const [printRedirectDefaultsSaving, setPrintRedirectDefaultsSaving] = useState(false);
    const [printRedirectDefaultsError, setPrintRedirectDefaultsError] = useState('');
    const [printRedirectDefaultsSaved, setPrintRedirectDefaultsSaved] = useState(false);
    const [printSettingsSaving, setPrintSettingsSaving] = useState(false);
    const [printSettingsError, setPrintSettingsError] = useState('');
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState('');
    const [taxForm, setTaxForm] = useState({
        name: '',
        rate: '',
        applyToAllProducts: true,
        isActive: true,
    });
    const [taxSaving, setTaxSaving] = useState(false);
    const [taxEdits, setTaxEdits] = useState({});
    const [branchTaxMap, setBranchTaxMap] = useState({});
    const [branchTaxLoading, setBranchTaxLoading] = useState(false);
    const [branchTaxError, setBranchTaxError] = useState('');
    const [branchTaxSaving, setBranchTaxSaving] = useState({});
    const [branchTaxDirty, setBranchTaxDirty] = useState({});
    const [newBranchTaxSelection, setNewBranchTaxSelection] = useState({});
    const [promotions, setPromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [promotionsError, setPromotionsError] = useState('');
    const [promotionSaving, setPromotionSaving] = useState(false);
    const [promotionEditingId, setPromotionEditingId] = useState(null);
    const [promotionForm, setPromotionForm] = useState(() => emptyPromotionForm());
    const [promotionProducts, setPromotionProducts] = useState([]);
    const [promotionProductsLoading, setPromotionProductsLoading] = useState(false);
    const [promotionSearch, setPromotionSearch] = useState('');
    const [priceRecommendationSettings, setPriceRecommendationSettings] = useState(() => emptyPriceRecommendationSettings());
    const [priceRecommendationLoading, setPriceRecommendationLoading] = useState(false);
    const [priceRecommendationSaving, setPriceRecommendationSaving] = useState(false);
    const [priceRecommendationError, setPriceRecommendationError] = useState('');
    const [priceRecommendationSaved, setPriceRecommendationSaved] = useState(false);
    const [posSoldOutMode, setPosSoldOutMode] = useState(POS_SOLD_OUT_MODE_NORMAL);
    const [posSoldOutLoading, setPosSoldOutLoading] = useState(false);
    const [posSoldOutSaving, setPosSoldOutSaving] = useState(false);
    const [posSoldOutError, setPosSoldOutError] = useState('');
    const [posSoldOutSaved, setPosSoldOutSaved] = useState(false);
    const { user } = useCurrentUser();
    const { confirm } = useConfirmDialog();
    const {
        preferences: myPreferences,
        loading: preferencesLoading,
        error: preferencesError,
        savePreferences: saveMyPreferences,
    } = useMyPreferences();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const canReadBranches = hasPermission(user, 'branch.read');
    const canUpdateBranch = hasPermission(user, 'branch.update');
    const canCreateBranch = hasPermission(user, 'branch.create');
    const canDeleteBranch = hasPermission(user, 'branch.delete');
    const canReadTaxes = hasPermission(user, 'tax.read');
    const canManageTaxes = hasPermission(user, 'tax.manage');
    const canReadUsers = hasPermission(user, 'user.read');
    const canReadProducts = hasPermission(user, 'product.read');
    const canCreateProduct = hasPermission(user, 'product.create');
    const canUpdateProduct = hasPermission(user, 'product.update');
    const canDeleteProduct = hasPermission(user, 'product.delete');
    const canReadCategories = hasAnyPermission(user, ['category.read', 'category.manage']);
    const canManagePurchaseOrderReasons = hasPermission(user, 'purchase_order.settings');
    const canManageClientPricing = hasPermission(user, 'client_pricing.manage');
    const canManageGlobalSettings = hasPermission(user, 'branch.access.any');
    const canReviewPurchaseOrders = hasAnyPermission(user, [
        'purchase_order.read',
        'purchase_order.update',
        'purchase_order.settings',
    ]);
    const canAccessRegisters = hasAnyPermission(user, [
        'cash_register.read',
        'cash_register.manage',
        'cash_register.assign_users',
    ]);
    const canConfigureRegisters = hasAnyPermission(user, [
        'cash_register.manage',
        'cash_register.assign_users',
    ]);
    const canManageRegisters = hasPermission(user, 'cash_register.manage');
    const canManageDte = hasPermission(user, 'dte.settings');
    const canUsePersonalPrintSettings = Boolean(user);

    const branchCreateAvailableUsers = useMemo(() => {
        if (!canReadUsers) return [];
        return (branchCreateUsers || [])
            .filter((entry) => hasPermission(entry, 'branch.access.any'))
            .sort((a, b) => (a.name || a.username || '').localeCompare(b.name || b.username || ''));
    }, [branchCreateUsers, canReadUsers]);

    const visibleTopics = useMemo(() => {
        const sectionAccess = {
            'registers-printing': canUsePersonalPrintSettings,
            'purchase-order-review': canReviewPurchaseOrders,
            'branch-directory': canReadBranches,
            'branch-price-sync': canManageGlobalSettings,
            'purchase-order-reasons': canManagePurchaseOrderReasons,
            'purchase-order-notifications': canManagePurchaseOrderReasons,
            'catalog-categories': canReadCategories,
            'catalog-price-list': canReadProducts,
            'catalog-promotions': canReadProducts,
            'catalog-pos-sales': canManageGlobalSettings,
            'catalog-price-recommendations': canReadProducts,
            'catalog-products': canReadProducts,
            'client-pricing-categories': canManageClientPricing,
            'taxes-manage': canReadTaxes,
            'dte-company': canManageDte,
            'dte-credentials': canManageDte,
            'dte-branches': canManageDte,
            'dte-cafs': canManageDte,
            'dte-automation': canManageDte,
            'users-access': canReadUsers,
            'registers-cash': canConfigureRegisters,
            'registers-print-defaults': canManageGlobalSettings,
            'registers-barcode': canManageGlobalSettings,
            'registers-pos-favs': canAccessRegisters && canReadProducts,
        };

        return SETTINGS_TOPICS
            .map((topic) => ({
                ...topic,
                sections: topic.sections.filter((section) => sectionAccess[section.id] === true),
            }))
            .filter((topic) => topic.sections.length > 0);
    }, [
        canAccessRegisters,
        canConfigureRegisters,
        canManageClientPricing,
        canManageDte,
        canManageGlobalSettings,
        canManagePurchaseOrderReasons,
        canReadBranches,
        canReadCategories,
        canReadProducts,
        canReadTaxes,
        canReadUsers,
        canReviewPurchaseOrders,
        canUsePersonalPrintSettings,
    ]);

    const activeTopicConfig = useMemo(
        () => visibleTopics.find((topic) => topic.id === activeTopic) || visibleTopics[0] || null,
        [activeTopic, visibleTopics]
    );

    const activeSectionConfig = useMemo(
        () =>
            activeTopicConfig?.sections.find((section) => section.id === activeSection)
            || activeTopicConfig?.sections[0]
            || null,
        [activeSection, activeTopicConfig]
    );

    const topicIcons = useMemo(
        () => ({
            personal: <FiUser className="text-base" />,
            branches: <RiStore2Line className="text-base" />,
            catalog: <FiBox className="text-base" />,
            clients: <FiUsers className="text-base" />,
            taxes: <FiPercent className="text-base" />,
            dte: <FiFileText className="text-base" />,
            users: <FiUsers className="text-base" />,
            registers: <FiSettings className="text-base" />,
        }),
        []
    );

    const sectionIcons = useMemo(
        () => ({
            'branch-directory': <RiStore2Line className="text-sm" />,
            'branch-price-sync': <FiSettings className="text-sm" />,
            'purchase-order-reasons': <FiFileText className="text-sm" />,
            'purchase-order-notifications': <FiBell className="text-sm" />,
            'purchase-order-review': <FiSettings className="text-sm" />,
            'catalog-categories': <FiBox className="text-sm" />,
            'catalog-price-list': <FiBox className="text-sm" />,
            'catalog-promotions': <FiPercent className="text-sm" />,
            'catalog-pos-sales': <FiSettings className="text-sm" />,
            'catalog-price-recommendations': <FiSettings className="text-sm" />,
            'catalog-products': <FiBox className="text-sm" />,
            'client-pricing-categories': <FiTag className="text-sm" />,
            'taxes-manage': <FiPercent className="text-sm" />,
            'dte-company': <FiFileText className="text-sm" />,
            'dte-credentials': <FiSettings className="text-sm" />,
            'dte-branches': <RiStore2Line className="text-sm" />,
            'dte-cafs': <FiFileText className="text-sm" />,
            'dte-automation': <FiSettings className="text-sm" />,
            'users-access': <FiUsers className="text-sm" />,
            'registers-cash': <FiHome className="text-sm" />,
            'registers-print-defaults': <FiPrinter className="text-sm" />,
            'registers-barcode': <FiSettings className="text-sm" />,
            'registers-printing': <FiPrinter className="text-sm" />,
            'registers-pos-favs': <FiStar className="text-sm" />,
        }),
        []
    );

    const isBranchScopedSection = Boolean(activeSectionConfig?.branchScoped);
    const activeBranches = useMemo(
        () => branches.filter((branch) => !branch.isDeleted),
        [branches]
    );

    const activeBranch = useMemo(
        () => activeBranches.find((branch) => String(branch.id) === String(activeBranchId)),
        [activeBranches, activeBranchId]
    );

    const clientPricingReferenceBranchId = useMemo(() => {
        if (activeBranchId) return String(activeBranchId);
        if (branchRequestConfig.resolvedMainBranchId) return String(branchRequestConfig.resolvedMainBranchId);
        return activeBranches[0]?.id ? String(activeBranches[0].id) : '';
    }, [activeBranchId, activeBranches, branchRequestConfig.resolvedMainBranchId]);

    const branchLabel = activeBranch?.branchName || 'Sucursal seleccionada';
    const branchPageSize = 8;
    const branchTotalPages = Math.max(1, Math.ceil(branches.length / branchPageSize));
    const currentBranchPage = Math.min(branchPage, branchTotalPages);
    const branchStartIndex = (currentBranchPage - 1) * branchPageSize;
    const branchEndIndex = Math.min(branchStartIndex + branchPageSize, branches.length);
    const pagedBranches = branches.slice(branchStartIndex, branchEndIndex);
    const effectiveMainBranchId = branchRequestConfig.resolvedMainBranchId ?? activeBranches[0]?.id ?? null;
    const promotionPriceOptions = useMemo(() => {
        const entries = [];
        (promotionProducts || []).forEach((product) => {
            (product.Varieties || []).forEach((variety) => {
                (variety.VarietyPrices || []).forEach((price) => {
                    if (price?.price === null || price?.price === undefined) return;
                    const id = Number(price.id);
                    if (!Number.isInteger(id)) return;
                    entries.push({
                        id,
                        label: `${product.productName || 'Producto'} - ${variety.varietyName || 'Variedad'} (${price.metricUnit || 'unidad'})`,
                        price: Number(price.price || 0),
                    });
                });
            });
        });
        const search = promotionSearch.trim().toLowerCase();
        if (!search) return entries;
        return entries.filter((entry) => entry.label.toLowerCase().includes(search));
    }, [promotionProducts, promotionSearch]);

    const selectedPromotionPriceIds = useMemo(
        () => new Set((promotionForm.varietyPriceIds || []).map((id) => Number(id))),
        [promotionForm.varietyPriceIds]
    );

    useEffect(() => {
        setMappings(getBarcodePrefixMappings());
    }, []);

    useEffect(() => {
        let mounted = true;
        const loadBarcodeRule = async () => {
            if (!canManageGlobalSettings) {
                setBarcodeRuleError('');
                setBarcodeRuleLoading(false);
                return;
            }
            setBarcodeRuleLoading(true);
            setBarcodeRuleError('');
            try {
                const data = await getBarcodePricingRule();
                if (!mounted) return;
                const thresholds = data?.thresholds || {};
                setBarcodePricingRule({
                    mode: data?.mode === 'subtotal' ? 'subtotal' : 'quantity',
                    thresholds: {
                        detalle: thresholds.detalle ?? '',
                        mayorista: thresholds.mayorista ?? '',
                        importador: thresholds.importador ?? '',
                    },
                });
                setBarcodeRuleSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBarcodeRuleError(err?.response?.data?.message || 'No se pudo cargar la regla global.');
            } finally {
                if (mounted) setBarcodeRuleLoading(false);
            }
        };
        loadBarcodeRule();
        return () => {
            mounted = false;
        };
    }, [canManageGlobalSettings]);

    const applyCategoryBarcodeResponse = (data) => {
        setCategoryBarcodes(Array.isArray(data?.categories) ? data.categories : []);
        setCategoryBarcodeInactiveCount(Number(data?.inactiveCount || 0));
    };

    const fetchCategoryBarcodes = async () => {
        setCategoryBarcodeLoading(true);
        setCategoryBarcodeError('');
        try {
            const data = await getCategoryBarcodes();
            applyCategoryBarcodeResponse(data);
            setCategoryBarcodeSaved(false);
        } catch (err) {
            setCategoryBarcodes([]);
            setCategoryBarcodeError(
                err?.response?.data?.message || 'No se pudieron cargar los barcodes de categorias.'
            );
        } finally {
            setCategoryBarcodeLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'registers-barcode') return;
        fetchCategoryBarcodes();
    }, [activeSectionConfig?.id]);

    const loadClientPricingCategories = useCallback(async () => {
        if (!canManageClientPricing) {
            setClientPricingCategories([]);
            setClientPricingCategoriesError('No tienes permisos para administrar descuentos de clientes.');
            return;
        }
        setClientPricingCategoriesLoading(true);
        setClientPricingCategoriesError('');
        try {
            const data = await getClientPricingCategories();
            const categories = Array.isArray(data?.categories) ? data.categories : [];
            setClientPricingCategories(categories);
            setSelectedClientPricingCategoryId((current) => {
                if (current && categories.some((category) => String(category.id) === String(current))) {
                    return current;
                }
                return categories[0]?.id ? String(categories[0].id) : '';
            });
        } catch (err) {
            setClientPricingCategories([]);
            setClientPricingCategoriesError(
                err?.response?.data?.message || 'No se pudieron cargar las categorias de descuento.'
            );
        } finally {
            setClientPricingCategoriesLoading(false);
        }
    }, [canManageClientPricing]);

    const loadClientPricingDiscountCatalog = useCallback(async () => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId) {
            setClientPricingDiscountRows([]);
            return;
        }
        setClientPricingDiscountsLoading(true);
        setClientPricingDiscountsError('');
        try {
            const data = await getClientPricingDiscountCatalog(selectedClientPricingCategoryId, {
                branchId: clientPricingReferenceBranchId,
                search: clientPricingSearch.trim() || undefined,
            });
            setClientPricingDiscountRows(Array.isArray(data?.rows) ? data.rows : []);
            setClientPricingRowSaveStatus({});
            setClientPricingDiscountsSaved(false);
        } catch (err) {
            setClientPricingDiscountRows([]);
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo cargar la matriz de descuentos.'
            );
        } finally {
            setClientPricingDiscountsLoading(false);
        }
    }, [
        canManageClientPricing,
        selectedClientPricingCategoryId,
        clientPricingReferenceBranchId,
        clientPricingSearch,
    ]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return;
        loadClientPricingCategories();
    }, [activeSectionConfig?.id, loadClientPricingCategories]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'client-pricing-categories') return undefined;
        const timer = window.setTimeout(() => {
            loadClientPricingDiscountCatalog();
        }, 250);
        return () => window.clearTimeout(timer);
    }, [activeSectionConfig?.id, loadClientPricingDiscountCatalog]);

    useEffect(() => {
        if (!canManageGlobalSettings) {
            setBranchRequestConfigLoading(false);
            setBranchRequestConfigError('');
            return undefined;
        }
        let mounted = true;
        const loadBranchRequestConfig = async () => {
            setBranchRequestConfigLoading(true);
            setBranchRequestConfigError('');
            try {
                const config = await getBranchRequestConfig();
                if (!mounted) return;
                setBranchRequestConfig({
                    mainBranchId: config?.mainBranchId ? String(config.mainBranchId) : '',
                    resolvedMainBranchId: config?.resolvedMainBranchId ?? null,
                    defaultQuotationCategoryIds: Array.isArray(config?.defaultQuotationCategoryIds)
                        ? config.defaultQuotationCategoryIds.map((id) => String(id))
                        : [],
                    defaultPurchaseOrderCategoryIds: Array.isArray(config?.defaultPurchaseOrderCategoryIds)
                        ? config.defaultPurchaseOrderCategoryIds.map((id) => String(id))
                        : [],
                });
                setBranchRequestConfigSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBranchRequestConfigError(
                    err?.response?.data?.message || 'No se pudo cargar la configuracion de solicitudes.'
                );
            } finally {
                if (mounted) setBranchRequestConfigLoading(false);
            }
        };
        loadBranchRequestConfig();
        return () => {
            mounted = false;
        };
    }, [canManageGlobalSettings]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'branch-price-sync') return;
        let mounted = true;
        const loadQuotationCategories = async () => {
            if (!canReadCategories) {
                setQuotationCategoryOptions([]);
                setQuotationCategoryError('No tienes permisos para ver categorias.');
                return;
            }
            setQuotationCategoryLoading(true);
            setQuotationCategoryError('');
            try {
                const data = await getCategories();
                if (!mounted) return;
                const categories = Array.isArray(data) ? data : normalizePaginatedResponse(data).data;
                setQuotationCategoryOptions(
                    categories
                        .filter((category) => category?.isActive !== false)
                        .map((category) => ({
                            id: String(category.id),
                            name: category.name || `Categoria ${category.id}`,
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
            } catch (err) {
                if (!mounted) return;
                setQuotationCategoryOptions([]);
                setQuotationCategoryError(
                    err?.response?.data?.message || 'No se pudieron cargar las categorias.'
                );
            } finally {
                if (mounted) setQuotationCategoryLoading(false);
            }
        };
        loadQuotationCategories();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canReadCategories]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'purchase-order-reasons') return;
        let mounted = true;
        const loadPurchaseOrderReasons = async () => {
            if (!canManagePurchaseOrderReasons) {
                setPurchaseOrderReasonsError('No tienes permisos para ver estos motivos.');
                return;
            }
            setPurchaseOrderReasonsLoading(true);
            setPurchaseOrderReasonsError('');
            try {
                const data = await getPurchaseOrderReasonsSettings();
                if (!mounted) return;
                setPurchaseOrderReasons({
                    reduce: Array.isArray(data?.reduce) ? data.reduce : [],
                    remove: Array.isArray(data?.remove) ? data.remove : [],
                    increase: Array.isArray(data?.increase) ? data.increase : [],
                });
                setPurchaseOrderReasonsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPurchaseOrderReasonsError(
                    err?.response?.data?.message || 'No se pudieron cargar los motivos.'
                );
            } finally {
                if (mounted) setPurchaseOrderReasonsLoading(false);
            }
        };
        loadPurchaseOrderReasons();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canManagePurchaseOrderReasons]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'purchase-order-notifications') return;
        let mounted = true;
        const loadPurchaseOrderNotificationSettings = async () => {
            if (!canManagePurchaseOrderReasons) {
                setPurchaseOrderNotificationsError('No tienes permisos para ver esta configuracion.');
                return;
            }
            setPurchaseOrderNotificationsLoading(true);
            setPurchaseOrderNotificationsError('');
            setPurchaseOrderNotificationsRoleWarning('');
            try {
                const settings = await getPurchaseOrderNotificationSettings();
                if (!mounted) return;
                const normalizedSettings = {
                    enabled: settings?.enabled !== false,
                    recipientRoleNames: normalizeRoleNameList(settings?.recipientRoleNames),
                    respectBranchAssignments: settings?.respectBranchAssignments !== false,
                    includeGlobalAdminUsers: settings?.includeGlobalAdminUsers !== false,
                };
                setPurchaseOrderNotificationSettings(normalizedSettings);
                let roles = [];
                let roleWarning = '';
                if (canReadUsers) {
                    try {
                        const rolesResponse = await getRoles({ pageSize: 0 });
                        if (!mounted) return;
                        roles = Array.isArray(rolesResponse)
                            ? rolesResponse
                            : normalizePaginatedResponse(rolesResponse).data;
                    } catch (roleError) {
                        if (!mounted) return;
                        roleWarning = roleError?.response?.data?.message
                            || 'No se pudo cargar el catalogo completo de roles. Se muestran los roles ya configurados.';
                    }
                } else {
                    roleWarning = 'No tienes permiso para cargar el catalogo completo de roles. Se muestran los roles ya configurados.';
                }
                const roleOptions = buildPurchaseOrderNotificationRoleOptions(
                    roles,
                    normalizedSettings.recipientRoleNames
                );
                setPurchaseOrderNotificationRoles(roleOptions);
                if (!roleOptions.some((role) => !role.configuredOnly) && !roleWarning) {
                    roleWarning = 'Se muestran los roles configurados. Revisa que los permisos base esten sincronizados en el servidor.';
                }
                setPurchaseOrderNotificationsRoleWarning(roleWarning);
                setPurchaseOrderNotificationsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPurchaseOrderNotificationsError(
                    err?.response?.data?.message || 'No se pudo cargar la configuracion de notificaciones.'
                );
            } finally {
                if (mounted) setPurchaseOrderNotificationsLoading(false);
            }
        };
        loadPurchaseOrderNotificationSettings();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canManagePurchaseOrderReasons, canReadUsers]);

    useEffect(() => {
        setPrintMode(getPrintMode());
    }, []);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'registers-print-defaults') return undefined;
        if (!canManageGlobalSettings) {
            setPrintRedirectDefaultsLoading(false);
            setPrintRedirectDefaultsError('');
            return undefined;
        }
        let mounted = true;
        const loadPrintRedirectDefaults = async () => {
            setPrintRedirectDefaultsLoading(true);
            setPrintRedirectDefaultsError('');
            try {
                const settings = await getPrintReceiptRedirectDefaults();
                if (!mounted) return;
                setPrintRedirectDefaults(normalizePrintReceiptRedirectDefaults(settings));
                setPrintRedirectDefaultsSaved(false);
            } catch (err) {
                if (!mounted) return;
                setPrintRedirectDefaultsError(
                    err?.response?.data?.message || 'No se pudo cargar el tiempo por defecto.'
                );
            } finally {
                if (mounted) setPrintRedirectDefaultsLoading(false);
            }
        };
        loadPrintRedirectDefaults();
        return () => {
            mounted = false;
        };
    }, [activeSectionConfig?.id, canManageGlobalSettings]);

    useEffect(() => {
        setPrintReceiptRedirect(normalizePrintReceiptRedirect(myPreferences?.printReceiptRedirect));
    }, [myPreferences?.printReceiptRedirect]);

    useEffect(() => {
        setPurchaseOrderReview(normalizePurchaseOrderReview(myPreferences?.purchaseOrderReview));
    }, [myPreferences?.purchaseOrderReview]);

    useEffect(() => {
        fetchTaxes();
    }, [canReadTaxes]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'taxes-manage') return;
        if (!activeBranches.length) return;
        fetchBranchTaxes();
    }, [activeSectionConfig?.id, activeBranches, taxes, canReadTaxes]);

    useEffect(() => {
        if (!taxes.length) {
            setTaxEdits({});
            return;
        }
        const nextEdits = {};
        taxes.forEach((tax) => {
            nextEdits[tax.id] = {
                name: tax.name || '',
                rate: tax.rate ?? '',
                applyToAllProducts: !!tax.applyToAllProducts,
                isActive: tax.isActive !== false,
            };
        });
        setTaxEdits(nextEdits);
    }, [taxes]);

    useEffect(() => {
        if (!branchCreateOpen) return;
        const defaults = {};
        taxes.forEach((tax) => {
            defaults[tax.id] = tax.isActive !== false;
        });
        setNewBranchTaxSelection(defaults);
    }, [branchCreateOpen, taxes]);

    useEffect(() => {
        setActiveBranchId(routeBranchId || localStorage.getItem('selectedBranchId') || '');
    }, [routeBranchId]);

    useEffect(() => {
        if (!branchCreateOpen) return;
        if (!canReadUsers) {
            setBranchCreateUsers([]);
            setBranchCreateUsersError('');
            setBranchCreateUsersLoading(false);
            return;
        }
        let isMounted = true;
        const fetchUsers = async () => {
            setBranchCreateUsersLoading(true);
            setBranchCreateUsersError('');
            try {
                const data = await getUsers({ pageSize: 0 });
                if (!isMounted) return;
                setBranchCreateUsers(normalizePaginatedResponse(data).data || []);
            } catch (err) {
                if (!isMounted) return;
                setBranchCreateUsers([]);
                setBranchCreateUsersError(err.message || 'No se pudieron cargar usuarios.');
            } finally {
                if (isMounted) setBranchCreateUsersLoading(false);
            }
        };
        fetchUsers();
        return () => {
            isMounted = false;
        };
    }, [branchCreateOpen, canReadUsers]);

    useEffect(() => {
        if (!visibleTopics.length) return;
        const params = new URLSearchParams(location.search);
        const topicParam = params.get('topic');
        const sectionParam = params.get('section');
        const requestedTopic = visibleTopics.find((topic) => topic.id === topicParam);
        const topicWithRequestedSection = sectionParam
            ? visibleTopics.find((topic) => topic.sections.some((section) => section.id === sectionParam))
            : null;
        const currentVisibleTopic = visibleTopics.find((topic) => topic.id === activeTopic);
        const requestedTopicHasSection = requestedTopic?.sections.some(
            (section) => section.id === sectionParam
        );
        const topicConfig = requestedTopicHasSection
            ? requestedTopic
            : topicWithRequestedSection || requestedTopic || currentVisibleTopic || visibleTopics[0];
        const nextTopic = topicConfig.id;
        const requestedSection = topicConfig.sections.find((section) => section.id === sectionParam);
        const currentVisibleSection = topicConfig.sections.find((section) => section.id === activeSection);
        const nextSection = (requestedSection || currentVisibleSection || topicConfig.sections[0]).id;

        if (nextTopic !== activeTopic) {
            setActiveTopic(nextTopic);
        }
        if (nextSection !== activeSection) {
            setActiveSection(nextSection);
        }
        if (topicParam !== nextTopic || sectionParam !== nextSection) {
            const nextParams = new URLSearchParams(location.search);
            nextParams.set('topic', nextTopic);
            nextParams.set('section', nextSection);
            navigate({ pathname: '/settings', search: nextParams.toString() }, { replace: true });
        }
    }, [location.search, activeTopic, activeSection, navigate, visibleTopics]);

    useEffect(() => {
        if (!activeTopicConfig?.sections?.length) return;
        if (activeTopicConfig.sections.some((section) => section.id === activeSection)) return;
        setActiveSection(activeTopicConfig.sections[0].id);
    }, [activeSection, activeTopicConfig]);

    const updateQueryParams = (nextTopic, nextSection) => {
        if (!nextTopic || !nextSection) return;
        const params = new URLSearchParams(location.search);
        params.set('topic', nextTopic);
        params.set('section', nextSection);
        navigate({ pathname: '/settings', search: params.toString() }, { replace: true });
    };

    const fetchBranches = async () => {
        if (!canReadBranches) {
            setBranches([]);
            setBranchError('No tienes permisos para ver sucursales.');
            return;
        }
        setBranchLoading(true);
        setBranchError('');
        try {
            const data = await getBranches({ pageSize: 0, includeDeleted: showDeletedBranches });
            const normalized = normalizePaginatedResponse(data);
            const list = normalized.data || [];
            setBranches(list);
            setPriceSyncError('');
            setPriceSyncSaved(false);
        } catch (err) {
            if (err.response?.status === 403) {
                setBranchError('No tienes permisos para ver sucursales.');
                setPriceSyncError('No tienes permisos para ver la configuracion de precios.');
            } else {
                setBranchError('No se pudieron cargar las sucursales.');
                setPriceSyncError('No se pudo cargar la configuracion de precios.');
            }
        } finally {
            setBranchLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, [canReadBranches, showDeletedBranches]);

    useEffect(() => {
        setBranchPage(1);
    }, [showDeletedBranches]);

    useEffect(() => {
        if (branchPage > branchTotalPages) {
            setBranchPage(branchTotalPages);
        }
    }, [branchPage, branchTotalPages]);

    useEffect(() => {
        if (!activeBranches.length) {
            if (activeBranchId) {
                setActiveBranchId('');
            }
            return;
        }
        const hasBranch = activeBranches.some((branch) => String(branch.id) === String(activeBranchId));
        if (hasBranch) return;
        const nextId = String(activeBranches[0].id);
        setActiveBranchId(nextId);
    }, [activeBranches, activeBranchId]);

    useEffect(() => {
        if (!activeBranchId || !activeBranches.length) return;
        const current = activeBranches.find((branch) => String(branch.id) === String(activeBranchId));
        if (!current) return;
        setPriceSync({
            sourceBranchId: current.priceSyncSourceBranchId
                ? String(current.priceSyncSourceBranchId)
                : '',
            adjustmentType: current.priceSyncAdjustmentType || '',
            adjustmentValue:
                current.priceSyncAdjustmentValue !== null
                && current.priceSyncAdjustmentValue !== undefined
                    ? String(current.priceSyncAdjustmentValue)
                    : '',
        });
        setPriceSyncSaved(false);
    }, [activeBranchId, activeBranches]);

    const sanitizeAdjustmentInput = (value) => value.replace(/[^\d-]/g, '');

    const handlePriceSyncSourceChange = (value) => {
        setPriceSync((prev) => ({
            sourceBranchId: value,
            adjustmentType: value ? (prev.adjustmentType || 'percent') : '',
            adjustmentValue: value ? prev.adjustmentValue : '',
        }));
        setPriceSyncSaved(false);
        setPriceSyncError('');
    };

    const handlePriceSyncTypeChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentType: value,
        }));
        setPriceSyncSaved(false);
    };

    const handlePriceSyncValueChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentValue: sanitizeAdjustmentInput(value),
        }));
        setPriceSyncSaved(false);
    };

    const parseAdjustmentValue = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return null;
        return Math.round(parsed);
    };

    const handleSavePriceSync = async () => {
        if (!activeBranchId) {
            setPriceSyncError('Sucursal no definida.');
            return;
        }
        if (!canUpdateBranch) {
            setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            return;
        }
        setPriceSyncLoading(true);
        try {
            const payload = {};
            if (!priceSync.sourceBranchId) {
                payload.priceSyncSourceBranchId = null;
                payload.priceSyncAdjustmentType = null;
                payload.priceSyncAdjustmentValue = null;
            } else {
                if (!PRICE_SYNC_TYPE_LABELS[priceSync.adjustmentType]) {
                    setPriceSyncError('Selecciona un tipo de ajuste valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                const parsedValue = parseAdjustmentValue(priceSync.adjustmentValue);
                if (parsedValue === null) {
                    setPriceSyncError('El ajuste debe ser un numero valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                payload.priceSyncSourceBranchId = Number(priceSync.sourceBranchId);
                payload.priceSyncAdjustmentType = priceSync.adjustmentType;
                payload.priceSyncAdjustmentValue = parsedValue;
            }
            await updateBranch(activeBranchId, payload);
            setBranches((prev) =>
                prev.map((branch) =>
                    String(branch.id) === String(activeBranchId)
                        ? {
                            ...branch,
                            priceSyncSourceBranchId: payload.priceSyncSourceBranchId,
                            priceSyncAdjustmentType: payload.priceSyncAdjustmentType,
                            priceSyncAdjustmentValue: payload.priceSyncAdjustmentValue,
                        }
                        : branch
                )
            );
            setPriceSyncError('');
            setPriceSyncSaved(true);
        } catch (err) {
            setPriceSyncSaved(false);
            if (err.response?.status === 403) {
                setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            } else {
                setPriceSyncError(
                    err.response?.data?.message || 'No se pudo guardar la configuracion de precios.'
                );
            }
        } finally {
            setPriceSyncLoading(false);
        }
    };

    const handleBranchRequestMainChange = (value) => {
        setBranchRequestConfig((prev) => ({
            ...prev,
            mainBranchId: value,
        }));
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleDefaultQuotationCategoryToggle = (categoryId) => {
        const normalizedId = String(categoryId);
        setBranchRequestConfig((prev) => {
            const current = new Set((prev.defaultQuotationCategoryIds || []).map(String));
            if (current.has(normalizedId)) {
                current.delete(normalizedId);
            } else {
                current.add(normalizedId);
            }
            return {
                ...prev,
                defaultQuotationCategoryIds: Array.from(current),
            };
        });
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleDefaultPurchaseOrderCategoryToggle = (categoryId) => {
        const normalizedId = String(categoryId);
        setBranchRequestConfig((prev) => {
            const current = new Set((prev.defaultPurchaseOrderCategoryIds || []).map(String));
            if (current.has(normalizedId)) {
                current.delete(normalizedId);
            } else {
                current.add(normalizedId);
            }
            return {
                ...prev,
                defaultPurchaseOrderCategoryIds: Array.from(current),
            };
        });
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleSaveBranchRequestConfig = async () => {
        if (!canManageGlobalSettings) {
            setBranchRequestConfigError('No tienes permisos para actualizar esta configuracion.');
            return;
        }
        setBranchRequestConfigLoading(true);
        setBranchRequestConfigError('');
        try {
            const payload = {
                mainBranchId: branchRequestConfig.mainBranchId
                    ? Number(branchRequestConfig.mainBranchId)
                    : null,
                defaultQuotationCategoryIds: (branchRequestConfig.defaultQuotationCategoryIds || [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id)),
                defaultPurchaseOrderCategoryIds: (branchRequestConfig.defaultPurchaseOrderCategoryIds || [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id)),
            };
            const saved = await updateBranchRequestConfig(payload);
            setBranchRequestConfig({
                mainBranchId: saved?.mainBranchId ? String(saved.mainBranchId) : '',
                resolvedMainBranchId: saved?.resolvedMainBranchId ?? null,
                defaultQuotationCategoryIds: Array.isArray(saved?.defaultQuotationCategoryIds)
                    ? saved.defaultQuotationCategoryIds.map((id) => String(id))
                    : [],
                defaultPurchaseOrderCategoryIds: Array.isArray(saved?.defaultPurchaseOrderCategoryIds)
                    ? saved.defaultPurchaseOrderCategoryIds.map((id) => String(id))
                    : [],
            });
            setBranchRequestConfigSaved(true);
        } catch (err) {
            setBranchRequestConfigSaved(false);
            setBranchRequestConfigError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de solicitudes.'
            );
        } finally {
            setBranchRequestConfigLoading(false);
        }
    };

    const updatePurchaseOrderReason = (group, index, patch) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: (prev[group] || []).map((entry, entryIndex) =>
                entryIndex === index ? { ...entry, ...patch } : entry
            ),
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const addPurchaseOrderReason = (group) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: [
                ...(prev[group] || []),
                {
                    id: '',
                    label: '',
                    active: true,
                },
            ],
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const removePurchaseOrderReason = (group, index) => {
        setPurchaseOrderReasons((prev) => ({
            ...prev,
            [group]: (prev[group] || []).filter((_, entryIndex) => entryIndex !== index),
        }));
        setPurchaseOrderReasonsSaved(false);
        setPurchaseOrderReasonsError('');
    };

    const handleSavePurchaseOrderReasons = async () => {
        if (!canManagePurchaseOrderReasons) return;
        setPurchaseOrderReasonsSaving(true);
        setPurchaseOrderReasonsError('');
        try {
            const response = await updatePurchaseOrderReasonsSettings(purchaseOrderReasons);
            const saved = response?.reasons || response;
            setPurchaseOrderReasons({
                reduce: Array.isArray(saved?.reduce) ? saved.reduce : [],
                remove: Array.isArray(saved?.remove) ? saved.remove : [],
                increase: Array.isArray(saved?.increase) ? saved.increase : [],
            });
            setPurchaseOrderReasonsSaved(true);
        } catch (err) {
            setPurchaseOrderReasonsSaved(false);
            setPurchaseOrderReasonsError(
                err?.response?.data?.message || 'No se pudieron guardar los motivos.'
            );
        } finally {
            setPurchaseOrderReasonsSaving(false);
        }
    };

    const updatePurchaseOrderNotificationSetting = (patch) => {
        setPurchaseOrderNotificationSettings((prev) => ({
            ...prev,
            ...patch,
        }));
        setPurchaseOrderNotificationsSaved(false);
        setPurchaseOrderNotificationsError('');
    };

    const togglePurchaseOrderNotificationRole = (roleName) => {
        const normalizedRole = String(roleName || '').trim();
        if (!normalizedRole) return;
        setPurchaseOrderNotificationSettings((prev) => {
            const current = new Set(prev.recipientRoleNames || []);
            if (current.has(normalizedRole)) current.delete(normalizedRole);
            else current.add(normalizedRole);
            return {
                ...prev,
                recipientRoleNames: Array.from(current),
            };
        });
        setPurchaseOrderNotificationsSaved(false);
        setPurchaseOrderNotificationsError('');
    };

    const handleSavePurchaseOrderNotifications = async () => {
        if (!canManagePurchaseOrderReasons) return;
        setPurchaseOrderNotificationsSaving(true);
        setPurchaseOrderNotificationsError('');
        try {
            const response = await updatePurchaseOrderNotificationSettings(purchaseOrderNotificationSettings);
            const settings = response?.settings || response;
            setPurchaseOrderNotificationSettings({
                enabled: settings?.enabled !== false,
                recipientRoleNames: Array.isArray(settings?.recipientRoleNames)
                    ? settings.recipientRoleNames
                    : [],
                respectBranchAssignments: settings?.respectBranchAssignments !== false,
                includeGlobalAdminUsers: settings?.includeGlobalAdminUsers !== false,
            });
            setPurchaseOrderNotificationsSaved(true);
        } catch (err) {
            setPurchaseOrderNotificationsSaved(false);
            setPurchaseOrderNotificationsError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de notificaciones.'
            );
        } finally {
            setPurchaseOrderNotificationsSaving(false);
        }
    };

    const refreshPurchaseOrderBrowserPushState = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            loading: true,
            error: '',
            message: '',
        }));
        try {
            const state = await loadBrowserPushState();
            setPurchaseOrderBrowserPushState({
                loading: false,
                saving: false,
                testing: false,
                supported: state.supported,
                configured: state.configured,
                permission: state.permission,
                serverSubscription: state.serverSubscription,
                error: '',
                message: '',
            });
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                loading: false,
                saving: false,
                testing: false,
                error: err?.message || 'No se pudo cargar el estado de este navegador.',
                message: '',
            }));
        }
    };

    const handleEnablePurchaseOrderBrowserPush = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            saving: true,
            testing: false,
            error: '',
            message: '',
        }));
        try {
            await enableBrowserPushNotifications();
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: 'Este navegador quedo suscrito a notificaciones.',
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                saving: false,
                error: err?.message || 'No se pudieron activar las notificaciones en este navegador.',
                message: '',
            }));
        }
    };

    const handleDisablePurchaseOrderBrowserPush = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            saving: true,
            testing: false,
            error: '',
            message: '',
        }));
        try {
            await disableBrowserPushNotifications();
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: 'Este navegador dejo de recibir notificaciones.',
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                saving: false,
                error: err?.message || 'No se pudieron desactivar las notificaciones en este navegador.',
                message: '',
            }));
        }
    };

    const handleSendPurchaseOrderTestNotification = async () => {
        setPurchaseOrderBrowserPushState((prev) => ({
            ...prev,
            testing: true,
            error: '',
            message: '',
        }));
        try {
            const response = await sendTestWebPushNotification();
            const result = response?.result || {};
            await refreshPurchaseOrderBrowserPushState();
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                message: result.sent > 0
                    ? 'Notificacion de prueba enviada.'
                    : getPurchaseOrderTestNotificationMessage(result.reason),
            }));
        } catch (err) {
            setPurchaseOrderBrowserPushState((prev) => ({
                ...prev,
                testing: false,
                error: err?.response?.data?.message || err?.message || 'No se pudo enviar la notificacion de prueba.',
                message: '',
            }));
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'purchase-order-notifications') return;
        if (!canManagePurchaseOrderReasons) return;
        refreshPurchaseOrderBrowserPushState();
    }, [activeSectionConfig?.id, canManagePurchaseOrderReasons]);

    const handlePrefixChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                prefix: sanitizeBarcodePrefixInput(value),
            };
            return next;
        });
        setSaved(false);
    };

    const handleModeChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                mode: value,
            };
            return next;
        });
        setSaved(false);
    };

    const handleAddMapping = () => {
        setMappings((prev) => [...prev, { prefix: '', mode: 'weight' }]);
        setSaved(false);
    };

    const handleRemoveMapping = (index) => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
        setSaved(false);
    };

    const validateMappings = (nextMappings) => {
        const normalized = nextMappings.map((item) => ({
            prefix: sanitizeBarcodePrefixInput(item?.prefix),
            mode: BARCODE_MODE_OPTIONS.includes(item?.mode) ? item.mode : 'weight',
        }));

        const prefixes = normalized.map((item) => item.prefix).filter(Boolean);
        if (prefixes.length === 0) {
            return { valid: false, message: 'Agrega al menos un prefijo.' };
        }

        const uniquePrefixes = new Set(prefixes);
        if (uniquePrefixes.size !== prefixes.length) {
            return { valid: false, message: 'Los prefijos deben ser unicos.' };
        }

        if (normalized.some((item) => !item.prefix)) {
            return { valid: false, message: 'Completa todos los prefijos.' };
        }

        if (normalized.some((item) => isReservedBarcodePrefix(item.prefix))) {
            return { valid: false, message: 'El prefijo 9 esta reservado para totales por categoria.' };
        }

        return { valid: true, normalized };
    };

    const handleSave = () => {
        const result = validateMappings(mappings);
        if (!result.valid) {
            setError(result.message);
            setSaved(false);
            return;
        }
        const savedMappings = saveBarcodePrefixMappings(result.normalized);
        setMappings(savedMappings);
        setError('');
        setSaved(true);
    };

    const handleReset = () => {
        const savedMappings = saveBarcodePrefixMappings(DEFAULT_BARCODE_PREFIX_MAPPINGS);
        setMappings(savedMappings);
        setError('');
        setSaved(false);
    };

    const handleBarcodeRuleModeChange = (mode) => {
        setBarcodePricingRule((prev) => ({
            ...prev,
            mode: mode === 'subtotal' ? 'subtotal' : 'quantity',
        }));
        setBarcodeRuleSaved(false);
    };

    const handleBarcodeRuleThresholdChange = (key, value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setBarcodePricingRule((prev) => ({
            ...prev,
            thresholds: {
                ...prev.thresholds,
                [key]: sanitized,
            },
        }));
        setBarcodeRuleSaved(false);
    };

    const handleSaveBarcodeRule = async () => {
        if (!canManageGlobalSettings) {
            setBarcodeRuleError('No tienes permisos para guardar esta regla.');
            return;
        }
        const payload = {
            mode: barcodePricingRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
            thresholds: {
                detalle: barcodePricingRule.thresholds.detalle === '' ? null : Number(barcodePricingRule.thresholds.detalle),
                mayorista: barcodePricingRule.thresholds.mayorista === '' ? null : Number(barcodePricingRule.thresholds.mayorista),
                importador: barcodePricingRule.thresholds.importador === '' ? null : Number(barcodePricingRule.thresholds.importador),
            },
        };
        setBarcodeRuleLoading(true);
        setBarcodeRuleError('');
        try {
            const response = await updateBarcodePricingRule(payload);
            const savedRule = response?.rule || payload;
            setBarcodePricingRule({
                mode: savedRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
                thresholds: {
                    detalle: savedRule?.thresholds?.detalle ?? '',
                    mayorista: savedRule?.thresholds?.mayorista ?? '',
                    importador: savedRule?.thresholds?.importador ?? '',
                },
            });
            setBarcodeRuleSaved(true);
        } catch (err) {
            setBarcodeRuleError(err?.response?.data?.message || 'No se pudo guardar la regla global.');
            setBarcodeRuleSaved(false);
        } finally {
            setBarcodeRuleLoading(false);
        }
    };

    const handleRegenerateCategoryBarcodes = async () => {
        if (!canManageGlobalSettings) {
            setCategoryBarcodeError('No tienes permisos para generar barcodes de categorias.');
            return;
        }
        setCategoryBarcodeRegenerating(true);
        setCategoryBarcodeError('');
        setCategoryBarcodeSaved(false);
        try {
            const data = await regenerateCategoryBarcodes();
            applyCategoryBarcodeResponse(data);
            setCategoryBarcodeSaved(true);
        } catch (err) {
            setCategoryBarcodeError(
                err?.response?.data?.error
                || err?.response?.data?.message
                || 'No se pudieron generar los barcodes de categorias.'
            );
        } finally {
            setCategoryBarcodeRegenerating(false);
        }
    };

    const handlePrintModeChange = (value) => {
        setPrintMode(value);
        setPrintModeSaved(false);
    };

    const handlePrintRedirectEnabledChange = (enabled) => {
        setPrintReceiptRedirect((prev) => ({
            ...prev,
            enabled,
        }));
        setPrintModeSaved(false);
    };

    const handlePrintRedirectDelayChange = (value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setPrintReceiptRedirect((prev) => ({
            ...prev,
            delaySeconds: sanitized,
        }));
        setPrintModeSaved(false);
    };

    const handlePrintRedirectDefaultDelayChange = (value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setPrintRedirectDefaults((prev) => ({
            ...prev,
            delaySeconds: sanitized,
        }));
        setPrintRedirectDefaultsSaved(false);
        setPrintRedirectDefaultsError('');
    };

    const savePrintRedirectDefaults = async (settings) => {
        const normalizedDefaults = normalizePrintReceiptRedirectDefaults(settings);
        setPrintRedirectDefaultsSaving(true);
        setPrintRedirectDefaultsError('');
        try {
            const response = await updatePrintReceiptRedirectDefaults(normalizedDefaults);
            const savedDefaults = normalizePrintReceiptRedirectDefaults(response?.settings || response);
            setPrintRedirectDefaults(savedDefaults);
            setPrintRedirectDefaultsSaved(true);
            if (!myPreferences?.hasCustomPrintReceiptRedirect) {
                setPrintReceiptRedirect((prev) => normalizePrintReceiptRedirect({
                    ...prev,
                    delaySeconds: savedDefaults.delaySeconds,
                }));
            }
        } catch (error) {
            setPrintRedirectDefaultsSaved(false);
            setPrintRedirectDefaultsError(
                error?.response?.data?.message || 'No se pudo guardar el tiempo por defecto.'
            );
        } finally {
            setPrintRedirectDefaultsSaving(false);
        }
    };

    const handleSavePrintRedirectDefaults = () => {
        savePrintRedirectDefaults(printRedirectDefaults);
    };

    const handleResetPrintRedirectDefaults = () => {
        savePrintRedirectDefaults(DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS);
    };

    const handlePurchaseOrderReviewChange = (field, value) => {
        setPurchaseOrderReview((prev) => ({
            ...prev,
            [field]: value,
        }));
        setPurchaseOrderReviewSaved(false);
    };

    const handleSavePurchaseOrderReview = async () => {
        const normalizedReview = normalizePurchaseOrderReview(purchaseOrderReview);
        setPurchaseOrderReviewSaving(true);
        setPurchaseOrderReviewError('');
        try {
            const savedPreferences = await saveMyPreferences({
                purchaseOrderReview: normalizedReview,
            });
            setPurchaseOrderReview(
                normalizePurchaseOrderReview(savedPreferences?.purchaseOrderReview || normalizedReview)
            );
            setPurchaseOrderReviewSaved(true);
        } catch (error) {
            setPurchaseOrderReviewSaved(false);
            setPurchaseOrderReviewError(error?.response?.data?.message || 'No se pudo guardar la configuracion.');
        } finally {
            setPurchaseOrderReviewSaving(false);
        }
    };

    const handleResetPurchaseOrderReview = async () => {
        setPurchaseOrderReviewSaving(true);
        setPurchaseOrderReviewError('');
        try {
            const savedPreferences = await saveMyPreferences({
                purchaseOrderReview: DEFAULT_PURCHASE_ORDER_REVIEW,
            });
            setPurchaseOrderReview(
                normalizePurchaseOrderReview(savedPreferences?.purchaseOrderReview || DEFAULT_PURCHASE_ORDER_REVIEW)
            );
            setPurchaseOrderReviewSaved(false);
        } catch (error) {
            setPurchaseOrderReviewError(error?.response?.data?.message || 'No se pudo restablecer la configuracion.');
        } finally {
            setPurchaseOrderReviewSaving(false);
        }
    };

    const handleSavePrintMode = async () => {
        const normalizedRedirect = normalizePrintReceiptRedirect(printReceiptRedirect);
        setPrintSettingsSaving(true);
        setPrintSettingsError('');
        try {
            const savedMode = savePrintMode(printMode);
            const savedPreferences = await saveMyPreferences({
                printReceiptRedirect: normalizedRedirect,
            });
            setPrintMode(savedMode);
            setPrintReceiptRedirect(
                normalizePrintReceiptRedirect(savedPreferences?.printReceiptRedirect || normalizedRedirect)
            );
            setPrintModeSaved(true);
        } catch (error) {
            setPrintModeSaved(false);
            setPrintSettingsError(error?.response?.data?.message || 'No se pudo guardar la configuracion.');
        } finally {
            setPrintSettingsSaving(false);
        }
    };

    const handleResetPrintMode = async () => {
        setPrintSettingsSaving(true);
        setPrintSettingsError('');
        try {
            const savedMode = savePrintMode(DEFAULT_PRINT_MODE);
            const savedPreferences = await saveMyPreferences({
                printReceiptRedirect: null,
            });
            setPrintMode(savedMode);
            setPrintReceiptRedirect(
                normalizePrintReceiptRedirect(savedPreferences?.printReceiptRedirect || DEFAULT_PRINT_RECEIPT_REDIRECT)
            );
            setPrintModeSaved(false);
        } catch (error) {
            setPrintSettingsError(error?.response?.data?.message || 'No se pudo restablecer la configuracion.');
        } finally {
            setPrintSettingsSaving(false);
        }
    };

    const fetchTaxes = async () => {
        if (!canReadTaxes) {
            setTaxes([]);
            setTaxesError('No tienes permisos para ver impuestos.');
            return;
        }
        setTaxesLoading(true);
        setTaxesError('');
        try {
            const data = await getTaxes();
            setTaxes(Array.isArray(data) ? data : []);
        } catch (err) {
            setTaxes([]);
            setTaxesError(err.response?.data?.message || 'No se pudieron cargar los impuestos.');
        } finally {
            setTaxesLoading(false);
        }
    };

    const fetchBranchTaxes = async () => {
        if (!canReadTaxes || !activeBranches.length) return;
        setBranchTaxLoading(true);
        setBranchTaxError('');
        try {
            const results = await Promise.all(
                activeBranches.map(async (branch) => ({
                    branchId: branch.id,
                    taxes: await getBranchTaxes(branch.id),
                }))
            );
            const nextMap = {};
            results.forEach((result) => {
                const branchMap = {};
                (result.taxes || []).forEach((tax) => {
                    branchMap[tax.id] = !!tax.branchActive;
                });
                nextMap[result.branchId] = branchMap;
            });
            setBranchTaxMap(nextMap);
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron cargar los impuestos por sucursal.');
        } finally {
            setBranchTaxLoading(false);
        }
    };

    const toDateTimeLocal = (value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        return date.toISOString().slice(0, 16);
    };

    const serializeDateTime = (value) => {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    };

    const fetchPromotions = async () => {
        if (!activeBranchId || !canReadProducts) {
            setPromotions([]);
            return;
        }
        setPromotionsLoading(true);
        setPromotionsError('');
        try {
            const data = await getPromotions(activeBranchId);
            setPromotions(Array.isArray(data) ? data : []);
        } catch (err) {
            setPromotions([]);
            setPromotionsError(err.response?.data?.message || 'No se pudieron cargar las promociones.');
        } finally {
            setPromotionsLoading(false);
        }
    };

    const fetchPromotionProducts = async () => {
        if (!activeBranchId || !canReadProducts) {
            setPromotionProducts([]);
            return;
        }
        setPromotionProductsLoading(true);
        try {
            const data = await getProducts(activeBranchId, { pageSize: 0 });
            setPromotionProducts(normalizePaginatedResponse(data).data || []);
        } catch (err) {
            setPromotionProducts([]);
            setPromotionsError(err.response?.data?.message || 'No se pudieron cargar los precios disponibles.');
        } finally {
            setPromotionProductsLoading(false);
        }
    };

    const resetPromotionForm = () => {
        setPromotionEditingId(null);
        setPromotionForm(emptyPromotionForm());
        setPromotionSearch('');
    };

    const handlePromotionFieldChange = (field, value) => {
        setPromotionForm((prev) => ({ ...prev, [field]: value }));
        setPromotionsError('');
    };

    const togglePromotionPriceId = (priceId) => {
        setPromotionForm((prev) => {
            const id = Number(priceId);
            const ids = new Set((prev.varietyPriceIds || []).map((entry) => Number(entry)));
            if (ids.has(id)) ids.delete(id);
            else ids.add(id);
            return { ...prev, varietyPriceIds: Array.from(ids) };
        });
    };

    const buildPromotionPayload = () => {
        const varietyPriceIds = (promotionForm.varietyPriceIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0);
        const config = promotionForm.type === 'pack'
            ? {
                varietyPriceIds,
                packQuantity: Number(promotionForm.packQuantity),
                packPrice: Number(promotionForm.packPrice),
            }
            : {
                varietyPriceIds,
                promoUnitPrice: Number(promotionForm.promoUnitPrice),
            };
        return {
            name: promotionForm.name.trim(),
            type: promotionForm.type,
            status: promotionForm.status,
            startsAt: serializeDateTime(promotionForm.startsAt),
            endsAt: serializeDateTime(promotionForm.endsAt),
            appliesToSales: !!promotionForm.appliesToSales,
            appliesToQuotations: !!promotionForm.appliesToQuotations,
            config,
        };
    };

    const handleSavePromotion = async () => {
        if (!activeBranchId || !canUpdateProduct) return;
        setPromotionSaving(true);
        setPromotionsError('');
        try {
            const payload = buildPromotionPayload();
            if (promotionEditingId) {
                await updatePromotion(activeBranchId, promotionEditingId, payload);
            } else {
                await createPromotion(activeBranchId, payload);
            }
            resetPromotionForm();
            await fetchPromotions();
        } catch (err) {
            setPromotionsError(err.response?.data?.message || 'No se pudo guardar la promocion.');
        } finally {
            setPromotionSaving(false);
        }
    };

    const handleEditPromotion = (promotion) => {
        const config = promotion?.config || {};
        setPromotionEditingId(promotion.id);
        setPromotionForm({
            name: promotion.name || '',
            type: promotion.type || 'temporary_price',
            status: promotion.status || 'active',
            startsAt: toDateTimeLocal(promotion.startsAt),
            endsAt: toDateTimeLocal(promotion.endsAt),
            appliesToSales: promotion.appliesToSales !== false,
            appliesToQuotations: promotion.appliesToQuotations !== false,
            varietyPriceIds: Array.isArray(config.varietyPriceIds) ? config.varietyPriceIds : [],
            promoUnitPrice: config.promoUnitPrice ?? '',
            packQuantity: config.packQuantity ?? '',
            packPrice: config.packPrice ?? '',
        });
        setPromotionsError('');
    };

    const handleTogglePromotionStatus = async (promotion) => {
        if (!canUpdateProduct || !promotion?.id) return;
        try {
            await updatePromotion(activeBranchId, promotion.id, {
                status: promotion.status === 'active' ? 'inactive' : 'active',
            });
            await fetchPromotions();
        } catch (err) {
            setPromotionsError(err.response?.data?.message || 'No se pudo actualizar la promocion.');
        }
    };

    const handleDeletePromotion = async (promotion) => {
        if (!canUpdateProduct || !promotion?.id) return;
        const confirmed = await confirm({
            title: 'Eliminar promocion',
            message: `Eliminar "${promotion.name}"?`,
            confirmLabel: 'Eliminar',
            cancelLabel: 'Cancelar',
            tone: 'danger',
        });
        if (!confirmed) return;
        try {
            await deletePromotion(activeBranchId, promotion.id);
            if (promotionEditingId === promotion.id) resetPromotionForm();
            await fetchPromotions();
        } catch (err) {
            setPromotionsError(err.response?.data?.message || 'No se pudo eliminar la promocion.');
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-promotions') return;
        fetchPromotions();
        fetchPromotionProducts();
    }, [activeSectionConfig?.id, activeBranchId, canReadProducts]);

    const fetchPosSoldOutDisplaySettings = async () => {
        setPosSoldOutLoading(true);
        setPosSoldOutError('');
        try {
            const settings = await getPosSoldOutDisplaySettings();
            setPosSoldOutMode(normalizePosSoldOutMode(settings?.soldOutMode));
            setPosSoldOutSaved(false);
        } catch (err) {
            setPosSoldOutMode(POS_SOLD_OUT_MODE_NORMAL);
            setPosSoldOutError(err.response?.data?.message || 'No se pudo cargar la configuracion POS.');
        } finally {
            setPosSoldOutLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-pos-sales') return;
        fetchPosSoldOutDisplaySettings();
    }, [activeSectionConfig?.id]);

    const handlePosSoldOutModeChange = (mode) => {
        setPosSoldOutMode(normalizePosSoldOutMode(mode));
        setPosSoldOutSaved(false);
        setPosSoldOutError('');
    };

    const handleSavePosSoldOutDisplaySettings = async () => {
        if (!canManageGlobalSettings) {
            setPosSoldOutError('No tienes permisos para guardar esta configuracion POS.');
            return;
        }
        setPosSoldOutSaving(true);
        setPosSoldOutError('');
        try {
            const savedSettings = await updatePosSoldOutDisplaySettings({
                soldOutMode: normalizePosSoldOutMode(posSoldOutMode),
            });
            setPosSoldOutMode(normalizePosSoldOutMode(savedSettings?.soldOutMode));
            setPosSoldOutSaved(true);
        } catch (err) {
            setPosSoldOutSaved(false);
            setPosSoldOutError(err.response?.data?.message || 'No se pudo guardar la configuracion POS.');
        } finally {
            setPosSoldOutSaving(false);
        }
    };

    const normalizePriceRecommendationSettingsForForm = (settings) => ({
        autoUpdatePrices: settings?.autoUpdatePrices !== false,
        rounding: {
            mode: 'hundreds_threshold',
            threshold: settings?.rounding?.threshold !== undefined && settings?.rounding?.threshold !== null
                ? String(settings.rounding.threshold)
                : '20',
        },
    });

    const fetchPriceRecommendationSettings = async () => {
        if (!activeBranchId || !canReadProducts) {
            setPriceRecommendationSettings(emptyPriceRecommendationSettings());
            return;
        }
        setPriceRecommendationLoading(true);
        setPriceRecommendationError('');
        try {
            const settings = await getPriceRecommendationSettings(activeBranchId);
            setPriceRecommendationSettings(normalizePriceRecommendationSettingsForForm(settings));
            setPriceRecommendationSaved(false);
        } catch (err) {
            setPriceRecommendationError(err.response?.data?.message || 'No se pudieron cargar las recomendaciones.');
        } finally {
            setPriceRecommendationLoading(false);
        }
    };

    useEffect(() => {
        if (activeSectionConfig?.id !== 'catalog-price-recommendations') return;
        fetchPriceRecommendationSettings();
    }, [activeSectionConfig?.id, activeBranchId, canReadProducts]);

    const updatePriceRecommendationRounding = (field, value) => {
        setPriceRecommendationSettings((prev) => ({
            ...prev,
            rounding: {
                ...prev.rounding,
                [field]: value,
            },
        }));
        setPriceRecommendationSaved(false);
        setPriceRecommendationError('');
    };

    const updatePriceRecommendationMode = (autoUpdatePrices) => {
        setPriceRecommendationSettings((prev) => ({
            ...prev,
            autoUpdatePrices,
        }));
        setPriceRecommendationSaved(false);
        setPriceRecommendationError('');
    };

    const buildPriceRecommendationPayload = () => ({
        autoUpdatePrices: priceRecommendationSettings.autoUpdatePrices !== false,
        rounding: {
            mode: 'hundreds_threshold',
            threshold: Number(priceRecommendationSettings.rounding?.threshold || 20),
        },
    });

    const validatePriceRecommendationPayload = (payload) => {
        const threshold = Number(payload.rounding.threshold);
        if (!Number.isFinite(threshold) || threshold < 0 || threshold > 99) {
            return 'El umbral de redondeo debe estar entre 0 y 99.';
        }
        return '';
    };

    const handleSavePriceRecommendationSettings = async () => {
        if (!activeBranchId || !canUpdateProduct) return;
        const payload = buildPriceRecommendationPayload();
        const validationError = validatePriceRecommendationPayload(payload);
        if (validationError) {
            setPriceRecommendationError(validationError);
            setPriceRecommendationSaved(false);
            return;
        }
        setPriceRecommendationSaving(true);
        setPriceRecommendationError('');
        try {
            const savedSettings = await updatePriceRecommendationSettings(activeBranchId, payload);
            setPriceRecommendationSettings(normalizePriceRecommendationSettingsForForm(savedSettings));
            setPriceRecommendationSaved(true);
        } catch (err) {
            setPriceRecommendationSaved(false);
            setPriceRecommendationError(err.response?.data?.message || 'No se pudieron guardar las recomendaciones.');
        } finally {
            setPriceRecommendationSaving(false);
        }
    };

    const resetClientPricingCategoryForm = () => {
        setClientPricingEditingId(null);
        setClientPricingCategoryForm({ name: '', description: '', isActive: true });
    };

    const handleSubmitClientPricingCategory = async (event) => {
        event.preventDefault();
        if (!canManageClientPricing) return;
        const name = String(clientPricingCategoryForm.name || '').trim();
        if (!name) {
            setClientPricingCategoriesError('Ingresa un nombre para la categoria.');
            return;
        }
        setClientPricingCategoriesLoading(true);
        setClientPricingCategoriesError('');
        try {
            const payload = {
                name,
                description: clientPricingCategoryForm.description,
                isActive: !!clientPricingCategoryForm.isActive,
            };
            const response = clientPricingEditingId
                ? await updateClientPricingCategory(clientPricingEditingId, payload)
                : await createClientPricingCategory(payload);
            const savedCategoryId = response?.category?.id || clientPricingEditingId;
            resetClientPricingCategoryForm();
            await loadClientPricingCategories();
            if (savedCategoryId) setSelectedClientPricingCategoryId(String(savedCategoryId));
        } catch (err) {
            setClientPricingCategoriesError(
                err?.response?.data?.message || 'No se pudo guardar la categoria.'
            );
        } finally {
            setClientPricingCategoriesLoading(false);
        }
    };

    const handleEditClientPricingCategory = (category) => {
        setClientPricingEditingId(category?.id || null);
        setClientPricingCategoryForm({
            name: category?.name || '',
            description: category?.description || '',
            isActive: category?.isActive !== false,
        });
    };

    const handleClientPricingRowDraftChange = (varietyPriceId, patch) => {
        setClientPricingDiscountRows((rows) => rows.map((row) => (
            Number(row.varietyPriceId) === Number(varietyPriceId)
                ? { ...row, ...patch, isDirty: true }
                : row
        )));
        setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'pending' }));
        setClientPricingDiscountsSaved(false);
    };

    const saveClientPricingRow = useCallback(async (row) => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId || !row?.varietyPriceId) return;
        const varietyPriceId = row.varietyPriceId;
        const payload = {
            discountType: row.discountType || 'amount',
            discountValue: row.discountValue || 0,
        };
        setClientPricingRowSaving((prev) => ({ ...prev, [varietyPriceId]: true }));
        setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'saving' }));
        setClientPricingDiscountsError('');
        try {
            await updateClientPricingDiscount(selectedClientPricingCategoryId, varietyPriceId, payload);
            setClientPricingDiscountRows((rows) => rows.map((currentRow) => {
                if (Number(currentRow.varietyPriceId) !== Number(varietyPriceId)) return currentRow;
                const stillSameDraft = String(currentRow.discountType || 'amount') === String(payload.discountType)
                    && String(currentRow.discountValue || 0) === String(payload.discountValue || 0);
                return stillSameDraft ? { ...currentRow, isDirty: false } : currentRow;
            }));
            setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'saved' }));
            setClientPricingDiscountsSaved(true);
        } catch (err) {
            setClientPricingDiscountsSaved(false);
            setClientPricingRowSaveStatus((prev) => ({ ...prev, [varietyPriceId]: 'error' }));
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo guardar el descuento.'
            );
        } finally {
            setClientPricingRowSaving((prev) => ({ ...prev, [varietyPriceId]: false }));
        }
    }, [canManageClientPricing, selectedClientPricingCategoryId]);

    useEffect(() => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId) return undefined;
        const dirtyRows = clientPricingDiscountRows.filter((row) => (
            row?.isDirty && !clientPricingRowSaving[row.varietyPriceId]
        ));
        if (!dirtyRows.length) return undefined;
        const timers = dirtyRows.map((row) => window.setTimeout(() => {
            saveClientPricingRow(row);
        }, 700));
        return () => {
            timers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [
        canManageClientPricing,
        selectedClientPricingCategoryId,
        clientPricingDiscountRows,
        clientPricingRowSaving,
        saveClientPricingRow,
    ]);

    const handleBulkClientPricingDiscounts = async (action = 'apply') => {
        if (!canManageClientPricing || !selectedClientPricingCategoryId) return;
        if (action !== 'clear' && !(Number(clientPricingBulkForm.discountValue) > 0)) {
            setClientPricingDiscountsError('Ingresa un descuento mayor a 0.');
            return;
        }
        const confirmed = action === 'clear'
            ? await confirm({
                title: 'Limpiar descuentos',
                message: 'Se limpiaran los descuentos de todos los precios mayorista visibles con el filtro actual.',
                confirmLabel: 'Limpiar',
                cancelLabel: 'Cancelar',
                tone: 'danger',
            })
            : true;
        if (!confirmed) return;
        setClientPricingBulkSaving(true);
        setClientPricingDiscountsError('');
        try {
            await bulkUpdateClientPricingDiscounts(selectedClientPricingCategoryId, {
                action,
                branchId: clientPricingReferenceBranchId,
                search: clientPricingSearch.trim(),
                discountType: clientPricingBulkForm.discountType,
                discountValue: clientPricingBulkForm.discountValue,
            });
            await loadClientPricingDiscountCatalog();
            setClientPricingDiscountsSaved(true);
        } catch (err) {
            setClientPricingDiscountsSaved(false);
            setClientPricingDiscountsError(
                err?.response?.data?.message || 'No se pudo aplicar la accion masiva.'
            );
        } finally {
            setClientPricingBulkSaving(false);
        }
    };

    const handleCreateTax = async () => {
        if (!canManageTaxes) return;
        const name = String(taxForm.name || '').trim();
        const rate = Number(taxForm.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await createTax({
                name,
                rate,
                applyToAllProducts: !!taxForm.applyToAllProducts,
                isActive: !!taxForm.isActive,
                isIncludedInPrice: true,
            });
            setTaxForm({ name: '', rate: '', applyToAllProducts: true, isActive: true });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo crear el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleTaxEditChange = (taxId, field, value) => {
        setTaxEdits((prev) => ({
            ...prev,
            [taxId]: {
                ...prev[taxId],
                [field]: value,
            },
        }));
    };

    const handleSaveTax = async (taxId) => {
        if (!canManageTaxes) return;
        const draft = taxEdits[taxId];
        if (!draft) return;
        const name = String(draft.name || '').trim();
        const rate = Number(draft.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await updateTax(taxId, {
                name,
                rate,
                applyToAllProducts: !!draft.applyToAllProducts,
                isActive: !!draft.isActive,
                isIncludedInPrice: true,
            });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo actualizar el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleBranchTaxToggle = (branchId, taxId) => {
        setBranchTaxMap((prev) => ({
            ...prev,
            [branchId]: {
                ...(prev[branchId] || {}),
                [taxId]: !(prev[branchId] || {})[taxId],
            },
        }));
        setBranchTaxDirty((prev) => ({ ...prev, [branchId]: true }));
    };

    const handleSaveBranchTaxes = async (branchId) => {
        if (!canManageTaxes) return;
        const branchMap = branchTaxMap[branchId] || {};
        const activeTaxIds = taxes.filter((tax) => branchMap[tax.id]).map((tax) => tax.id);
        setBranchTaxSaving((prev) => ({ ...prev, [branchId]: true }));
        try {
            const result = await updateBranchTaxes(branchId, { activeTaxIds });
            const updated = Array.isArray(result?.taxes) ? result.taxes : [];
            const nextBranchMap = {};
            updated.forEach((tax) => {
                nextBranchMap[tax.id] = !!tax.branchActive;
            });
            setBranchTaxMap((prev) => ({ ...prev, [branchId]: nextBranchMap }));
            setBranchTaxDirty((prev) => ({ ...prev, [branchId]: false }));
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron guardar los impuestos.');
        } finally {
            setBranchTaxSaving((prev) => ({ ...prev, [branchId]: false }));
        }
    };

    const handleBranchSelect = (branchId) => {
        const nextId = String(branchId || '');
        if (!nextId || nextId === String(activeBranchId)) return;
        setActiveBranchId(nextId);
        navigate({ pathname: '/settings', search: location.search }, { replace: true });
    };

    const handleTopicSelect = (topicId) => {
        const topicConfig = visibleTopics.find((topic) => topic.id === topicId) || visibleTopics[0];
        if (!topicConfig) return;
        const nextSection = topicConfig.sections.find((section) => section.id === activeSection)
            ? activeSection
            : topicConfig.sections[0].id;
        setActiveTopic(topicConfig.id);
        setActiveSection(nextSection);
        updateQueryParams(topicConfig.id, nextSection);
    };

    const handleSectionSelect = (sectionId) => {
        setActiveSection(sectionId);
        updateQueryParams(activeTopicConfig?.id, sectionId);
    };

    const resetBranchCreateForm = () => {
        setNewBranchName('');
        setNewBranchRegisters([buildRegisterTemplate(0)]);
        setBranchCreateError('');
        setNewBranchTaxSelection({});
    };

    const handleOpenCreateBranch = () => {
        if (!canCreateBranch) return;
        setBranchCreateNotice('');
        resetBranchCreateForm();
        setBranchCreateOpen(true);
    };

    const handleCloseCreateBranch = () => {
        if (branchCreating) return;
        setBranchCreateOpen(false);
        resetBranchCreateForm();
    };

    const updateRegister = (index, updater) => {
        setNewBranchRegisters((prev) =>
            prev.map((register, idx) => (idx === index ? updater(register) : register))
        );
    };

    const handleRegisterNameChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, name: value }));
    };

    const handleRegisterActiveChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, isActive: value }));
    };

    const handleRegisterMaxOpenSalesChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, maxOpenSales: value }));
    };

    const handleRegisterDiscountApproverChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, discountApproverUserId: value }));
    };

    const toggleRegisterClientType = (index, type) => {
        updateRegister(index, (register) => {
            const current = Array.isArray(register.allowedClientTypes)
                ? register.allowedClientTypes
                : [];
            const next = current.includes(type)
                ? current.filter((entry) => entry !== type)
                : [...current, type];
            return { ...register, allowedClientTypes: next };
        });
    };

    const toggleRegisterUser = (index, userId) => {
        updateRegister(index, (register) => {
            const current = Array.isArray(register.users) ? register.users : [];
            const next = current.includes(userId)
                ? current.filter((entry) => entry !== userId)
                : [...current, userId];
            return { ...register, users: next };
        });
    };

    const handleAddRegister = () => {
        setNewBranchRegisters((prev) => [...prev, buildRegisterTemplate(prev.length)]);
    };

    const handleRemoveRegister = (index) => {
        setNewBranchRegisters((prev) =>
            prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev
        );
    };

    const handleCreateBranch = async () => {
        if (!canCreateBranch) return;
        if (!canManageRegisters) {
            setBranchCreateError('No tienes permisos para crear cajas.');
            return;
        }
        const trimmedName = newBranchName.trim();
        if (!trimmedName) {
            setBranchCreateError('El nombre de la sucursal es obligatorio.');
            return;
        }
        const registerPayloads = [];
        const seenNames = new Set();
        for (let index = 0; index < newBranchRegisters.length; index += 1) {
            const register = newBranchRegisters[index];
            const trimmedName = register?.name?.trim();
            if (!trimmedName) {
                setBranchCreateError(`El nombre de la caja ${index + 1} es obligatorio.`);
                return;
            }
            const parsedMaxOpenSales = Number.parseInt(register?.maxOpenSales, 10);
            if (!Number.isInteger(parsedMaxOpenSales) || parsedMaxOpenSales < 1) {
                setBranchCreateError(
                    `El limite de ventas abiertas en "${trimmedName}" debe ser un entero >= 1.`
                );
                return;
            }
            const nameKey = trimmedName.toLowerCase();
            if (seenNames.has(nameKey)) {
                continue;
            }
            seenNames.add(nameKey);
            const allowedTypes = Array.isArray(register?.allowedClientTypes)
                ? register.allowedClientTypes.filter((type) => CLIENT_TYPE_LABELS[type])
                : [];
            const selectedUsers = canReadUsers
                ? (Array.isArray(register?.users) ? register.users : [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id))
                    .map((userId) => ({ userId, isActive: true }))
                : [];
            const approverId = register?.discountApproverUserId
                ? Number(register.discountApproverUserId)
                : null;
            registerPayloads.push({
                name: trimmedName,
                isActive: register?.isActive ?? true,
                allowedClientTypes: allowedTypes,
                discountApproverUserId: Number.isInteger(approverId) ? approverId : null,
                maxOpenSales: parsedMaxOpenSales,
                users: selectedUsers,
            });
        }
        if (!registerPayloads.length) {
            setBranchCreateError('Agrega al menos una caja.');
            return;
        }
        setBranchCreating(true);
        setBranchCreateError('');
        try {
            const created = await createBranch({ branchName: trimmedName });
            const branchId = created?.id;
            if (!branchId) {
                throw new Error('No se recibio el ID de la sucursal.');
            }
            if (canManageTaxes && Object.keys(newBranchTaxSelection).length) {
                const activeTaxIds = Object.entries(newBranchTaxSelection)
                    .filter(([, isActive]) => isActive)
                    .map(([taxId]) => Number(taxId))
                    .filter((taxId) => Number.isInteger(taxId));
                await updateBranchTaxes(branchId, { activeTaxIds });
            }
            const results = await Promise.allSettled(
                registerPayloads.map((payload) => createCashRegister(branchId, payload))
            );
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            if (successCount === 0) {
                setBranchCreateNotice(
                    'Sucursal creada, pero no se pudo crear ninguna caja. Ve a "Cajas" para crear al menos una.'
                );
            } else if (successCount < registerPayloads.length) {
                setBranchCreateNotice(
                    'Sucursal creada, pero algunas cajas no se pudieron crear.'
                );
            } else {
                setBranchCreateNotice('');
            }
            setBranchCreateOpen(false);
            resetBranchCreateForm();
            fetchBranches();
        } catch (err) {
            setBranchCreateError(
                err.response?.data?.message
                || err.message
                || 'No se pudo crear la sucursal.'
            );
        } finally {
            setBranchCreating(false);
        }
    };

    const handleUpdateBranch = async (branch) => {
        if (!canUpdateBranch) return;
        if (!branch) return;
        const trimmedName = editedBranchName.trim();
        if (!trimmedName) return;
        setBranchError('');
        try {
            await updateBranch(branch.id, { branchName: trimmedName });
            setEditingBranchId(null);
            setEditedBranchName('');
            fetchBranches();
        } catch (err) {
            setBranchError(err.response?.data?.message || 'No se pudo actualizar la sucursal.');
        }
    };

    const handleDeleteBranch = async (branch) => {
        if (!canDeleteBranch) return;
        if (!branch) return;
        if (branch.isDeleted) return;
        if (effectiveMainBranchId && String(branch.id) === String(effectiveMainBranchId)) {
            setBranchError('La sucursal principal no se puede eliminar.');
            return;
        }
        const label = branch?.branchName ? ` "${branch.branchName}"` : '';
        const approved = await confirm({
            title: 'Eliminar sucursal',
            message: `Eliminar sucursal${label}?`,
            confirmLabel: 'Eliminar',
            cancelLabel: 'Cancelar',
            tone: 'danger',
        });
        if (!approved) return;
        setBranchError('');
        setBranchCreateNotice('');
        try {
            const result = await deleteBranch(branch.id);
            if (result?.softDeleted) {
                setBranchCreateNotice('Sucursal marcada como eliminada porque tiene datos asociados.');
            }
            if (String(branch.id) === String(activeBranchId)) {
                setActiveBranchId('');
            }
            fetchBranches();
        } catch (err) {
            setBranchError(err.response?.data?.message || 'No se pudo eliminar la sucursal.');
        }
    };

    const handleRestoreBranch = async (branch) => {
        if (!canUpdateBranch) return;
        if (!branch?.isDeleted) return;
        setBranchError('');
        setBranchCreateNotice('');
        try {
            await restoreBranch(branch.id);
            setBranchCreateNotice('Sucursal restaurada.');
            fetchBranches();
        } catch (err) {
            setBranchError(err.response?.data?.message || 'No se pudo restaurar la sucursal.');
        }
    };

    const sourceBranchOptions = activeBranches.filter(
        (branch) => String(branch.id) !== String(activeBranchId)
    );
    const isSyncEnabled = Boolean(priceSync.sourceBranchId);
    const canViewPriceSync = canManageGlobalSettings;
    const canEditPriceSync = canManageGlobalSettings && canUpdateBranch;

    const renderBranchTabs = () => {
        if (!isBranchScopedSection) return null;
        if (branchLoading) {
            return (
                <div className="text-sm text-gray-500">
                    <InlineLoading label="Cargando sucursales" />
                </div>
            );
        }
        if (!activeBranchId && !activeBranches.length) {
            return (
                <div className="text-sm text-gray-500">
                    Selecciona una sucursal para ver esta configuracion.
                </div>
            );
        }
        if (!canReadBranches || !activeBranches.length) {
            return (
                <div className="text-xs text-gray-500">
                    Sucursal actual: <span className="font-medium text-gray-700">{branchLabel}</span>
                </div>
            );
        }
        return (
            <HorizontalPillTabs
                items={activeBranches.map((branch) => ({
                    id: branch.id,
                    label: branch.branchName,
                }))}
                activeId={activeBranchId}
                onSelect={handleBranchSelect}
            />
        );
    };

    const renderUsersAccess = () => {
        if (!canReadUsers) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver usuarios.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Gestiona roles y accesos desde esta vista.
                </div>
                <BranchUsersPage embedded globalView />
            </div>
        );
    };

    const renderBranchDirectory = () => {
        if (!canReadBranches) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver sucursales.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h2 className="text-md font-semibold">Listado de sucursales</h2>
                        <p className="text-xs text-gray-500">
                            Crea, edita o elimina sucursales desde esta vista.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={showDeletedBranches}
                                onChange={(e) => setShowDeletedBranches(e.target.checked)}
                            />
                            Mostrar eliminadas
                        </label>
                        {canCreateBranch && (
                            <div className="flex flex-col md:flex-row md:items-center gap-2">
                            <button
                                type="button"
                                onClick={handleOpenCreateBranch}
                                disabled={!canManageRegisters}
                                className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${
                                    !canManageRegisters ? 'opacity-60 cursor-not-allowed' : ''
                                }`}
                            >
                                Nueva sucursal
                            </button>
                            {!canManageRegisters && (
                                <span className="text-xs text-amber-600">
                                    Necesitas permisos de cajas para crear sucursales.
                                </span>
                            )}
                            </div>
                        )}
                    </div>
                </div>
                {branchError && (
                    <div className="text-sm text-red-600">{branchError}</div>
                )}
                {branchCreateNotice && (
                    <div className="text-sm text-amber-600">{branchCreateNotice}</div>
                )}
                <div className="bg-white border rounded-lg divide-y">
                    {branchLoading && (
                        <div className="p-4 text-sm text-gray-500">
                            <InlineLoading label="Cargando sucursales" />
                        </div>
                    )}
                    {!branchLoading && branches.length === 0 && (
                        <div className="p-4 text-sm text-gray-500">
                            No hay sucursales registradas.
                        </div>
                    )}
                    {pagedBranches.map((branch) => {
                        const isMainBranch = effectiveMainBranchId
                            && String(branch.id) === String(effectiveMainBranchId);

                        return (
                            <div
                                key={branch.id}
                                className={`p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
                                    branch.isDeleted ? 'bg-gray-50 opacity-80' : ''
                                }`}
                            >
                                <div className="flex-1">
                                    {editingBranchId === branch.id ? (
                                        <input
                                            type="text"
                                            value={editedBranchName}
                                            onChange={(e) => setEditedBranchName(e.target.value)}
                                            className="w-full px-2 py-1 border rounded"
                                        />
                                    ) : (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-medium text-gray-800">{branch.branchName}</span>
                                            {isMainBranch && (
                                                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-700 border border-blue-100">
                                                    Principal
                                                </span>
                                            )}
                                            {branch.isDeleted && (
                                                <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700 border border-red-100">
                                                    Eliminada
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500">ID {branch.id}</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {canUpdateBranch && !branch.isDeleted && (
                                        editingBranchId === branch.id ? (
                                            <button
                                                onClick={() => handleUpdateBranch(branch)}
                                                className="bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded text-white text-sm"
                                            >
                                                Guardar
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setEditingBranchId(branch.id);
                                                    setEditedBranchName(branch.branchName);
                                                }}
                                                className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-sm"
                                            >
                                                Editar
                                            </button>
                                        )
                                    )}
                                    {branch.isDeleted ? (
                                        canUpdateBranch && (
                                            <button
                                                type="button"
                                                onClick={() => handleRestoreBranch(branch)}
                                                className="bg-amber-500 hover:bg-amber-600 px-3 py-1 rounded text-white text-sm"
                                            >
                                                Restaurar
                                            </button>
                                        )
                                    ) : canDeleteBranch && (
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteBranch(branch)}
                                            disabled={isMainBranch || branch.isDeleted}
                                            title={isMainBranch ? 'La sucursal principal no se puede eliminar.' : undefined}
                                            className={`px-3 py-1 rounded text-white text-sm ${
                                                isMainBranch
                                                    ? 'bg-gray-300 cursor-not-allowed'
                                                    : 'bg-red-500 hover:bg-red-600'
                                            }`}
                                        >
                                            Eliminar
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {branches.length > branchPageSize && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500">
                        <div>
                            Mostrando {branches.length === 0 ? 0 : branchStartIndex + 1}-{branchEndIndex} de {branches.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setBranchPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentBranchPage === 1}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <span>
                                {currentBranchPage} / {branchTotalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setBranchPage((prev) => Math.min(prev + 1, branchTotalPages))}
                                disabled={currentBranchPage === branchTotalPages}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPurchaseOrderReasons = () => {
        if (!canManagePurchaseOrderReasons) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para administrar motivos de ordenes.
                </div>
            );
        }

        return (
            <div className="border rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-md font-semibold">Motivos de ajuste de ordenes</h2>
                        <p className="text-xs text-gray-500">
                            Se usan al reducir, eliminar o aumentar items en ordenes de compra de clientes.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleSavePurchaseOrderReasons}
                        disabled={purchaseOrderReasonsSaving || purchaseOrderReasonsLoading}
                        className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                    >
                        {purchaseOrderReasonsSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>

                {purchaseOrderReasonsLoading ? (
                    <InlineLoading label="Cargando motivos" />
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        {PURCHASE_ORDER_REASON_GROUPS.map((group) => (
                            <div key={group.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <div className="mb-3 flex items-center justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-gray-800">{group.label}</h3>
                                    <button
                                        type="button"
                                        onClick={() => addPurchaseOrderReason(group.id)}
                                        className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                                    >
                                        Agregar
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {(purchaseOrderReasons[group.id] || []).map((reason, index) => (
                                        <div key={`${group.id}-${index}`} className="rounded border border-gray-200 bg-white p-2">
                                            <div className="grid grid-cols-1 gap-2">
                                                <input
                                                    value={reason.label || ''}
                                                    onChange={(event) => updatePurchaseOrderReason(group.id, index, { label: event.target.value })}
                                                    placeholder="Motivo"
                                                    className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                />
                                                <input
                                                    value={reason.id || ''}
                                                    onChange={(event) => updatePurchaseOrderReason(group.id, index, { id: event.target.value })}
                                                    placeholder="id"
                                                    className="rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-600"
                                                />
                                                <div className="flex items-center justify-between gap-2">
                                                    <label className="flex items-center gap-2 text-xs text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={reason.active !== false}
                                                            onChange={(event) => updatePurchaseOrderReason(group.id, index, { active: event.target.checked })}
                                                        />
                                                        Activo
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => removePurchaseOrderReason(group.id, index)}
                                                        className="rounded bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100"
                                                    >
                                                        Quitar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {!(purchaseOrderReasons[group.id] || []).length && (
                                        <p className="text-xs text-gray-500">Sin motivos configurados.</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {purchaseOrderReasonsError && (
                    <p className="text-sm text-red-600 mt-3">{purchaseOrderReasonsError}</p>
                )}
                {purchaseOrderReasonsSaved && !purchaseOrderReasonsError && (
                    <p className="text-sm text-green-600 mt-3">Motivos guardados.</p>
                )}
            </div>
        );
    };

    const renderPurchaseOrderNotifications = () => {
        if (!canManagePurchaseOrderReasons) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para administrar notificaciones de ordenes.
                </div>
            );
        }
        const selectedRoleNames = new Set(purchaseOrderNotificationSettings.recipientRoleNames || []);

        return (
            <div className="border rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-md font-semibold">Notificaciones push de ordenes</h2>
                        <p className="text-xs text-gray-500">
                            Define quien recibe avisos del navegador cuando un cliente crea una orden de compra.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleSavePurchaseOrderNotifications}
                        disabled={purchaseOrderNotificationsSaving || purchaseOrderNotificationsLoading}
                        className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                    >
                        {purchaseOrderNotificationsSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>

                {purchaseOrderNotificationsLoading ? (
                    <InlineLoading label="Cargando notificaciones" />
                ) : (
                    <div className="space-y-4">
                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.enabled !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ enabled: event.target.checked })}
                            />
                            Activar notificaciones de nuevas ordenes
                        </label>

                        <div className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Este navegador</h3>
                                    <p className="text-xs text-gray-500">
                                        Activa el permiso del navegador y registra este dispositivo para recibir avisos.
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.supported
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.supported ? 'Compatible' : 'No compatible'}
                                        </span>
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.configured
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.configured ? 'Servidor configurado' : 'Servidor sin claves push'}
                                        </span>
                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
                                            Permiso: {purchaseOrderBrowserPushState.permission}
                                        </span>
                                        <span className={`rounded-full px-2 py-1 ${
                                            purchaseOrderBrowserPushState.serverSubscription
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {purchaseOrderBrowserPushState.serverSubscription ? 'Suscrito' : 'No suscrito'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={refreshPurchaseOrderBrowserPushState}
                                        disabled={
                                            purchaseOrderBrowserPushState.loading
                                            || purchaseOrderBrowserPushState.saving
                                            || purchaseOrderBrowserPushState.testing
                                        }
                                        className="rounded border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-60"
                                    >
                                        {purchaseOrderBrowserPushState.loading ? 'Cargando...' : 'Actualizar'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSendPurchaseOrderTestNotification}
                                        disabled={
                                            !purchaseOrderBrowserPushState.serverSubscription
                                            || purchaseOrderBrowserPushState.loading
                                            || purchaseOrderBrowserPushState.saving
                                            || purchaseOrderBrowserPushState.testing
                                        }
                                        className="rounded bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
                                    >
                                        {purchaseOrderBrowserPushState.testing ? 'Enviando...' : 'Enviar prueba'}
                                    </button>
                                    {purchaseOrderBrowserPushState.serverSubscription ? (
                                        <button
                                            type="button"
                                            onClick={handleDisablePurchaseOrderBrowserPush}
                                            disabled={
                                                purchaseOrderBrowserPushState.loading
                                                || purchaseOrderBrowserPushState.saving
                                                || purchaseOrderBrowserPushState.testing
                                            }
                                            className="rounded bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-60"
                                        >
                                            {purchaseOrderBrowserPushState.saving ? 'Guardando...' : 'Desactivar este navegador'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleEnablePurchaseOrderBrowserPush}
                                            disabled={
                                                !purchaseOrderBrowserPushState.supported
                                                || !purchaseOrderBrowserPushState.configured
                                                || purchaseOrderBrowserPushState.loading
                                                || purchaseOrderBrowserPushState.saving
                                                || purchaseOrderBrowserPushState.testing
                                            }
                                            className="rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                                        >
                                            {purchaseOrderBrowserPushState.saving ? 'Guardando...' : 'Activar en este navegador'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {purchaseOrderBrowserPushState.error && (
                                <p className="mt-3 text-sm text-red-600">{purchaseOrderBrowserPushState.error}</p>
                            )}
                            {purchaseOrderBrowserPushState.message && !purchaseOrderBrowserPushState.error && (
                                <p className="mt-3 text-sm text-green-600">{purchaseOrderBrowserPushState.message}</p>
                            )}
                            {!purchaseOrderBrowserPushState.loading
                                && purchaseOrderBrowserPushState.supported
                                && !purchaseOrderBrowserPushState.configured && (
                                <p className="mt-3 text-xs text-amber-700">
                                    Falta configurar las claves VAPID en el backend antes de pedir el permiso del navegador.
                                </p>
                            )}
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <div className="mb-3 flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Roles destinatarios</h3>
                                    <p className="text-xs text-gray-500">
                                        Solo se muestran roles con permiso para leer ordenes de compra.
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">{selectedRoleNames.size} seleccionados</span>
                            </div>
                            {purchaseOrderNotificationRoles.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {purchaseOrderNotificationRoles.map((role) => (
                                        <label
                                            key={role.name}
                                            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRoleNames.has(role.name)}
                                                onChange={() => togglePurchaseOrderNotificationRole(role.name)}
                                            />
                                            <span>{role.name}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                                    No hay roles destinatarios configurados.
                                </p>
                            )}
                            {purchaseOrderNotificationsRoleWarning && (
                                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                                    {purchaseOrderNotificationsRoleWarning}
                                </p>
                            )}
                        </div>

                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.respectBranchAssignments !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ respectBranchAssignments: event.target.checked })}
                            />
                            Respetar asignaciones de sucursal
                        </label>

                        <label className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={purchaseOrderNotificationSettings.includeGlobalAdminUsers !== false}
                                onChange={(event) => updatePurchaseOrderNotificationSetting({ includeGlobalAdminUsers: event.target.checked })}
                            />
                            Incluir usuarios administradores globales
                        </label>
                    </div>
                )}

                {purchaseOrderNotificationsError && (
                    <p className="text-sm text-red-600 mt-3">{purchaseOrderNotificationsError}</p>
                )}
                {purchaseOrderNotificationsSaved && !purchaseOrderNotificationsError && (
                    <p className="text-sm text-green-600 mt-3">Configuracion de notificaciones guardada.</p>
                )}
            </div>
        );
    };

    const renderPriceSync = () => {
        if (!canViewPriceSync) {
            return (
                <div className="border rounded-lg p-4">
                    <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        No tienes permisos para ver la configuracion de precios.
                    </p>
                </div>
            );
        }

        const selectedDefaultCategoryIds = new Set(
            (branchRequestConfig.defaultQuotationCategoryIds || []).map(String)
        );
        const selectedDefaultPurchaseOrderCategoryIds = new Set(
            (branchRequestConfig.defaultPurchaseOrderCategoryIds || []).map(String)
        );

        return (
            <div className="space-y-4">
                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                            <p className="text-xs text-gray-500">
                                Replica cambios de precios desde otra sucursal con ajuste.
                            </p>
                        </div>
                        {canEditPriceSync && (
                            <button
                                onClick={handleSavePriceSync}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                                disabled={priceSyncLoading}
                            >
                                Guardar configuracion
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Casa matriz</span>
                            <select
                                value={priceSync.sourceBranchId}
                                onChange={(e) => handlePriceSyncSourceChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!canEditPriceSync}
                            >
                                <option value="">Sin sincronizacion</option>
                                {sourceBranchOptions.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branchName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Tipo ajuste</span>
                            <select
                                value={priceSync.adjustmentType}
                                onChange={(e) => handlePriceSyncTypeChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            >
                                <option value="">Selecciona</option>
                                {Object.keys(PRICE_SYNC_TYPE_LABELS).map((key) => (
                                    <option key={key} value={key}>
                                        {PRICE_SYNC_TYPE_LABELS[key]}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Ajuste</span>
                            <input
                                type="text"
                                value={priceSync.adjustmentValue}
                                onChange={(e) => handlePriceSyncValueChange(e.target.value)}
                                placeholder="0"
                                className="w-40 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            />
                            <span className="text-xs text-gray-500">Usa negativo para descuento.</span>
                        </div>
                        <p className="text-xs text-gray-500">
                            El ajuste se aplica a cada cambio de precio. La copia inicial requiere backfill.
                        </p>
                    </div>
                    {!canEditPriceSync && (
                        <p className="text-xs text-gray-500 mt-2">
                            Solo lectura: necesitas permisos para editar.
                        </p>
                    )}
                    {priceSyncError && (
                        <p className="text-sm text-red-600 mt-3">{priceSyncError}</p>
                    )}
                    {priceSyncSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
                    )}
                </div>

                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">Sucursal principal de solicitudes</h2>
                            <p className="text-xs text-gray-500">
                                Define la sucursal que recibe solicitudes y gestiona envios salientes.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleSaveBranchRequestConfig}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                            disabled={branchRequestConfigLoading}
                        >
                            Guardar
                        </button>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Principal</span>
                            <select
                                value={branchRequestConfig.mainBranchId}
                                onChange={(e) => handleBranchRequestMainChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={branchRequestConfigLoading}
                            >
                                <option value="">Automatico (sucursal mas antigua)</option>
                                {activeBranches.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branchName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <p className="text-xs text-gray-500">
                            Principal efectiva: {
                                activeBranches.find((branch) => String(branch.id) === String(branchRequestConfig.resolvedMainBranchId))?.branchName
                                || 'No definida'
                            }
                        </p>
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Categorias por defecto del portal</h3>
                                    <p className="text-xs text-gray-500">
                                        Se aplican al habilitar el portal de cotizaciones de un cliente.
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {selectedDefaultCategoryIds.size} seleccionadas
                                </span>
                            </div>
                            {quotationCategoryLoading ? (
                                <InlineLoading label="Cargando categorias" />
                            ) : quotationCategoryError ? (
                                <p className="text-xs text-red-600">{quotationCategoryError}</p>
                            ) : quotationCategoryOptions.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {quotationCategoryOptions.map((category) => (
                                        <label
                                            key={category.id}
                                            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedDefaultCategoryIds.has(String(category.id))}
                                                onChange={() => handleDefaultQuotationCategoryToggle(category.id)}
                                                disabled={branchRequestConfigLoading}
                                            />
                                            <span>{category.name}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-500">
                                    No hay categorias activas disponibles.
                                </p>
                            )}
                            {!selectedDefaultCategoryIds.size && !quotationCategoryLoading && !quotationCategoryError && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mt-3">
                                    Sin categorias seleccionadas: los clientes habilitados veran el catalogo vacio hasta configurar sus categorias.
                                </p>
                            )}
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Categorias por defecto para ordenes</h3>
                                    <p className="text-xs text-gray-500">
                                        Se aplican al habilitar el portal de ordenes de compra de un cliente.
                                    </p>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {selectedDefaultPurchaseOrderCategoryIds.size} seleccionadas
                                </span>
                            </div>
                            {quotationCategoryLoading ? (
                                <InlineLoading label="Cargando categorias" />
                            ) : quotationCategoryError ? (
                                <p className="text-xs text-red-600">{quotationCategoryError}</p>
                            ) : quotationCategoryOptions.length ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {quotationCategoryOptions.map((category) => (
                                        <label
                                            key={category.id}
                                            className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedDefaultPurchaseOrderCategoryIds.has(String(category.id))}
                                                onChange={() => handleDefaultPurchaseOrderCategoryToggle(category.id)}
                                                disabled={branchRequestConfigLoading}
                                            />
                                            <span>{category.name}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-500">
                                    No hay categorias activas disponibles.
                                </p>
                            )}
                            {!selectedDefaultPurchaseOrderCategoryIds.size && !quotationCategoryLoading && !quotationCategoryError && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mt-3">
                                    Sin categorias seleccionadas: los clientes habilitados veran vacio el catalogo de ordenes hasta configurar sus categorias.
                                </p>
                            )}
                        </div>
                    </div>
                    {branchRequestConfigError && (
                        <p className="text-sm text-red-600 mt-3">{branchRequestConfigError}</p>
                    )}
                    {branchRequestConfigSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion de solicitudes guardada.</p>
                    )}
                </div>
            </div>
        );
    };

    const renderRegisters = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar cajas.
                </div>
            );
        }
        if (!canConfigureRegisters) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver la configuracion de cajas.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Administra cajas, usuarios y limites desde esta vista.
                </div>
                <CashRegistersPage branchId={activeBranchId} embedded />
            </div>
        );
    };

    const renderPosFavs = () => (
        <PosSlotSettingsPanel
            branchId={activeBranchId}
            branchLabel={branchLabel}
            preferences={myPreferences}
            preferencesLoading={preferencesLoading}
            preferencesError={preferencesError}
            savePreferences={saveMyPreferences}
            canAccessRegisters={canAccessRegisters}
            canReadProducts={canReadProducts}
            canReadCategories={canReadCategories}
        />
    );

    const renderCatalogProducts = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar productos.
                </div>
            );
        }
        if (!canReadProducts) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver productos.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Gestiona productos, precios y variedades desde esta vista.
                </div>
                <ProductList
                    branchId={activeBranchId}
                    embedded
                    canReadProducts={canReadProducts}
                    canCreateProduct={canCreateProduct}
                    canUpdateProduct={canUpdateProduct}
                    canDeleteProduct={canDeleteProduct}
                />
            </div>
        );
    };

    const renderCatalogCategories = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar categorias.
                </div>
            );
        }
        if (!canReadCategories) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver categorias.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Configura categorias y disponibilidad por sucursal.
                </div>
                <CategoriesAdminPage branchId={activeBranchId} embedded />
            </div>
        );
    };

    const renderCatalogPromotions = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar promociones.
                </div>
            );
        }
        if (!canReadProducts) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver promociones.
                </div>
            );
        }

        const selectedCount = promotionForm.varietyPriceIds.length;
        const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;
        const formatDate = (value) => {
            if (!value) return 'Sin fecha';
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? 'Sin fecha' : date.toLocaleString('es-CL');
        };

        return (
            <div className="space-y-6">
                <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-md font-semibold">Promociones</h2>
                            <p className="text-xs text-gray-500">
                                Reglas automaticas por sucursal para ventas y cotizaciones.
                            </p>
                        </div>
                        {promotionsLoading && <InlineLoading label="Cargando promociones" />}
                    </div>

                    {promotionsError && (
                        <p className="text-sm text-red-600">{promotionsError}</p>
                    )}

                    <div className="max-w-full overflow-x-auto">
                        <table className="min-w-[760px] w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Vigencia</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Aplica</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {promotions.map((promotion) => (
                                    <tr key={promotion.id}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-gray-800">{promotion.name}</div>
                                            <div className={`text-xs ${promotion.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                                                {promotion.status === 'active' ? 'Activa' : 'Inactiva'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            {PROMOTION_TYPE_LABELS[promotion.type] || promotion.type}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-600">
                                            <div>{formatDate(promotion.startsAt)}</div>
                                            <div>{formatDate(promotion.endsAt)}</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-600">
                                            {[
                                                promotion.appliesToSales ? 'Ventas' : null,
                                                promotion.appliesToQuotations ? 'Cotizaciones' : null,
                                            ].filter(Boolean).join(', ') || 'Sin destino'}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex justify-end gap-2">
                                                {canUpdateProduct && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="px-2 py-1 rounded border border-gray-300 text-xs hover:bg-gray-50"
                                                            onClick={() => handleTogglePromotionStatus(promotion)}
                                                        >
                                                            {promotion.status === 'active' ? 'Desactivar' : 'Activar'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="px-2 py-1 rounded border border-blue-300 text-blue-700 text-xs hover:bg-blue-50"
                                                            onClick={() => handleEditPromotion(promotion)}
                                                        >
                                                            Editar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50"
                                                            onClick={() => handleDeletePromotion(promotion)}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!promotionsLoading && promotions.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-3 py-4 text-center text-xs text-gray-500">
                                            No hay promociones registradas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {canUpdateProduct && (
                    <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold">
                                    {promotionEditingId ? 'Editar promocion' : 'Nueva promocion'}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Selecciona precios especificos y la regla que debe ganar contra el descuento manual.
                                </p>
                            </div>
                            {promotionEditingId && (
                                <button
                                    type="button"
                                    className="px-3 py-1 rounded border border-gray-300 text-xs hover:bg-gray-50"
                                    onClick={resetPromotionForm}
                                >
                                    Nueva
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <label className="text-xs text-gray-700 md:col-span-2">
                                Nombre
                                <input
                                    type="text"
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.name}
                                    onChange={(e) => handlePromotionFieldChange('name', e.target.value)}
                                    disabled={promotionSaving}
                                />
                            </label>
                            <label className="text-xs text-gray-700">
                                Tipo
                                <select
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.type}
                                    onChange={(e) => handlePromotionFieldChange('type', e.target.value)}
                                    disabled={promotionSaving}
                                >
                                    <option value="temporary_price">Precio temporal</option>
                                    <option value="pack">Pack</option>
                                </select>
                            </label>
                            <label className="text-xs text-gray-700">
                                Estado
                                <select
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.status}
                                    onChange={(e) => handlePromotionFieldChange('status', e.target.value)}
                                    disabled={promotionSaving}
                                >
                                    <option value="active">Activa</option>
                                    <option value="inactive">Inactiva</option>
                                </select>
                            </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <label className="text-xs text-gray-700">
                                Inicio
                                <input
                                    type="datetime-local"
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.startsAt}
                                    onChange={(e) => handlePromotionFieldChange('startsAt', e.target.value)}
                                    disabled={promotionSaving}
                                />
                            </label>
                            <label className="text-xs text-gray-700">
                                Termino
                                <input
                                    type="datetime-local"
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.endsAt}
                                    onChange={(e) => handlePromotionFieldChange('endsAt', e.target.value)}
                                    disabled={promotionSaving}
                                />
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
                                <input
                                    type="checkbox"
                                    checked={promotionForm.appliesToSales}
                                    onChange={(e) => handlePromotionFieldChange('appliesToSales', e.target.checked)}
                                    disabled={promotionSaving}
                                />
                                Ventas
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
                                <input
                                    type="checkbox"
                                    checked={promotionForm.appliesToQuotations}
                                    onChange={(e) => handlePromotionFieldChange('appliesToQuotations', e.target.checked)}
                                    disabled={promotionSaving}
                                />
                                Cotizaciones
                            </label>
                        </div>

                        {promotionForm.type === 'temporary_price' ? (
                            <label className="block text-xs text-gray-700 max-w-xs">
                                Precio unitario promocional
                                <input
                                    type="number"
                                    min="0"
                                    className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                    value={promotionForm.promoUnitPrice}
                                    onChange={(e) => handlePromotionFieldChange('promoUnitPrice', e.target.value)}
                                    disabled={promotionSaving}
                                />
                            </label>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
                                <label className="text-xs text-gray-700">
                                    Cantidad del pack
                                    <input
                                        type="number"
                                        min="2"
                                        className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                        value={promotionForm.packQuantity}
                                        onChange={(e) => handlePromotionFieldChange('packQuantity', e.target.value)}
                                        disabled={promotionSaving}
                                    />
                                </label>
                                <label className="text-xs text-gray-700">
                                    Precio del pack
                                    <input
                                        type="number"
                                        min="0"
                                        className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                        value={promotionForm.packPrice}
                                        onChange={(e) => handlePromotionFieldChange('packPrice', e.target.value)}
                                        disabled={promotionSaving}
                                    />
                                </label>
                            </div>
                        )}

                        <div className="space-y-2">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div className="text-xs font-semibold text-gray-600 uppercase">
                                    Precios objetivo ({selectedCount})
                                </div>
                                <input
                                    type="text"
                                    className="border border-gray-300 rounded p-2 text-sm md:w-80"
                                    placeholder="Buscar precio..."
                                    value={promotionSearch}
                                    onChange={(e) => setPromotionSearch(e.target.value)}
                                    disabled={promotionProductsLoading}
                                />
                            </div>
                            {promotionProductsLoading ? (
                                <InlineLoading label="Cargando precios" />
                            ) : (
                                <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-gray-100">
                                    {promotionPriceOptions.map((option) => (
                                        <label key={option.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-gray-50">
                                            <span className="flex items-center gap-2 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPromotionPriceIds.has(option.id)}
                                                    onChange={() => togglePromotionPriceId(option.id)}
                                                    disabled={promotionSaving}
                                                />
                                                <span className="truncate">{option.label}</span>
                                            </span>
                                            <span className="text-xs text-gray-600 flex-none">{formatCurrency(option.price)}</span>
                                        </label>
                                    ))}
                                    {promotionPriceOptions.length === 0 && (
                                        <div className="px-3 py-4 text-center text-xs text-gray-500">
                                            No hay precios disponibles.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                                onClick={handleSavePromotion}
                                disabled={promotionSaving}
                            >
                                {promotionSaving ? 'Guardando...' : 'Guardar promocion'}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
                                onClick={resetPromotionForm}
                                disabled={promotionSaving}
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCatalogPosSales = () => (
        <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h2 className="text-md font-semibold">Venta POS</h2>
                        <p className="text-xs text-gray-500">
                            Define como se muestran los productos agotados en el buscador de caja.
                        </p>
                    </div>
                    {posSoldOutLoading && <InlineLoading label="Cargando configuracion POS" />}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {POS_SOLD_OUT_DISPLAY_OPTIONS.map((option) => {
                        const isActive = posSoldOutMode === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={`text-left border rounded-lg p-3 transition disabled:opacity-60 ${
                                    isActive
                                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'
                                }`}
                                onClick={() => handlePosSoldOutModeChange(option.value)}
                                disabled={posSoldOutLoading || posSoldOutSaving}
                            >
                                <span className="block text-sm font-semibold">{option.label}</span>
                                <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                            </button>
                        );
                    })}
                </div>

                {posSoldOutError && (
                    <p className="text-sm text-red-600">{posSoldOutError}</p>
                )}
                {posSoldOutSaved && !posSoldOutError && (
                    <p className="text-sm text-green-600">Configuracion POS guardada.</p>
                )}

                <button
                    type="button"
                    className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
                    onClick={handleSavePosSoldOutDisplaySettings}
                    disabled={posSoldOutSaving || posSoldOutLoading}
                >
                    {posSoldOutSaving ? 'Guardando...' : 'Guardar configuracion POS'}
                </button>
            </div>
        </div>
    );

    const renderCatalogPriceRecommendations = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para configurar recomendaciones.
                </div>
            );
        }
        if (!canReadProducts) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver recomendaciones de precios.
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h2 className="text-md font-semibold">Recomendaciones de precios</h2>
                            <p className="text-xs text-gray-500">
                                El margen se configura en cada precio de variedad. Aqui se define si la ejecucion actualiza precios o solo deja recomendaciones.
                            </p>
                        </div>
                        {priceRecommendationLoading && <InlineLoading label="Cargando recomendaciones" />}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                            {
                                value: true,
                                label: 'Actualizar precios automaticamente',
                                description: 'La ejecucion guarda los precios calculados y registra los cambios aplicados.',
                            },
                            {
                                value: false,
                                label: 'Solo recomendar precios',
                                description: 'La ejecucion calcula y registra recomendaciones sin cambiar la lista de precios.',
                            },
                        ].map((option) => {
                            const isSelected = (priceRecommendationSettings.autoUpdatePrices !== false) === option.value;
                            return (
                                <button
                                    key={String(option.value)}
                                    type="button"
                                    className={`text-left border rounded-lg p-3 transition disabled:opacity-60 ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200'
                                    }`}
                                    onClick={() => updatePriceRecommendationMode(option.value)}
                                    disabled={!canUpdateProduct || priceRecommendationLoading || priceRecommendationSaving}
                                >
                                    <span className="block text-sm font-semibold">{option.label}</span>
                                    <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="text-xs text-gray-700">
                            Umbral redondeo centenas
                            <input
                                type="number"
                                min="0"
                                max="99"
                                className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                value={priceRecommendationSettings.rounding?.threshold ?? '20'}
                                onChange={(e) => updatePriceRecommendationRounding('threshold', e.target.value)}
                                disabled={!canUpdateProduct || priceRecommendationLoading || priceRecommendationSaving}
                            />
                        </label>
                        <div className="text-xs text-gray-500 flex items-end">
                            Con 20: terminaciones 0-20 bajan, 21-99 suben a la siguiente centena. Los precios fijos no se redondean.
                        </div>
                    </div>

                    {priceRecommendationError && (
                        <p className="text-sm text-red-600">{priceRecommendationError}</p>
                    )}
                    {priceRecommendationSaved && !priceRecommendationError && (
                        <p className="text-sm text-green-600">Recomendaciones guardadas.</p>
                    )}
                    {canUpdateProduct ? (
                        <button
                            type="button"
                            className="px-4 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-60"
                            onClick={handleSavePriceRecommendationSettings}
                            disabled={priceRecommendationSaving || priceRecommendationLoading}
                        >
                            {priceRecommendationSaving ? 'Guardando...' : 'Guardar recomendaciones'}
                        </button>
                    ) : (
                        <p className="text-xs text-gray-500">Solo lectura: necesitas permiso para actualizar productos.</p>
                    )}
                </div>
            </div>
        );
    };

    const renderBarcodes = () => (
        <div className="border rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                    <h2 className="text-md font-semibold">Codigos de barras</h2>
                    <p className="text-xs text-gray-500">
                        Prefijos de 1 digito para codigos de 13 digitos. El prefijo 9 esta reservado.
                    </p>
                </div>
                <button
                    onClick={handleAddMapping}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                >
                    + Prefijo
                </button>
            </div>

            <div className="space-y-3">
                {mappings.map((mapping, index) => (
                    <div
                        key={`${mapping.prefix}-${index}`}
                        className="flex items-center gap-3"
                    >
                        <input
                            type="text"
                            value={mapping.prefix}
                            placeholder="Prefijo"
                            inputMode="numeric"
                            maxLength={BARCODE_PREFIX_MAX_LENGTH}
                            pattern="[0-9]*"
                            onChange={(e) => handlePrefixChange(index, e.target.value)}
                            className="w-20 p-2 border border-gray-300 rounded"
                        />
                        <select
                            value={mapping.mode}
                            onChange={(e) => handleModeChange(index, e.target.value)}
                            className="flex-1 p-2 border border-gray-300 rounded"
                        >
                            {BARCODE_MODE_OPTIONS.map((mode) => (
                                <option key={mode} value={mode}>
                                    {MODE_LABELS[mode] || mode}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleRemoveMapping(index)}
                            className="text-red-600 text-sm hover:text-red-700"
                        >
                            Quitar
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-6 border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                <div>
                    <h3 className="text-sm font-semibold">Regla global de selección automática</h3>
                    <p className="text-xs text-gray-500">
                        Define si la selección se basa en cantidad acumulada por variedad o subtotal del documento.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="text-xs text-gray-700">
                        Modo
                        <select
                            className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                            value={barcodePricingRule.mode}
                            onChange={(e) => handleBarcodeRuleModeChange(e.target.value)}
                            disabled={barcodeRuleLoading}
                        >
                            <option value="quantity">Cantidad por variedad</option>
                            <option value="subtotal">Subtotal documento</option>
                        </select>
                    </label>
                    <div className="text-xs text-gray-500 flex items-end">
                        Si más de un tipo califica, se aplica el precio calificado más barato.
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(CLIENT_TYPE_LABELS).map(([type, label]) => (
                        <label key={type} className="text-xs text-gray-700">
                            Umbral {label}
                            <input
                                type="text"
                                inputMode="numeric"
                                className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                value={barcodePricingRule.thresholds[type] ?? ''}
                                onChange={(e) => handleBarcodeRuleThresholdChange(type, e.target.value)}
                                placeholder="Sin configurar"
                                disabled={barcodeRuleLoading}
                            />
                        </label>
                    ))}
                </div>
                {barcodeRuleError && (
                    <p className="text-xs text-red-600">{barcodeRuleError}</p>
                )}
                {barcodeRuleSaved && !barcodeRuleError && (
                    <p className="text-xs text-green-600">Regla global guardada.</p>
                )}
                <div>
                    <button
                        type="button"
                        onClick={handleSaveBarcodeRule}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                        disabled={barcodeRuleLoading}
                    >
                        {barcodeRuleLoading ? 'Guardando...' : 'Guardar regla'}
                    </button>
                </div>
            </div>

            <div className="mt-6 border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                        <h3 className="text-sm font-semibold">Barcodes por categoria</h3>
                        <p className="text-xs text-gray-500">
                            Catalogo persistente del identificador de 6 digitos por categoria. El voucher agrega los 6 digitos del total.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleRegenerateCategoryBarcodes}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                        disabled={categoryBarcodeLoading || categoryBarcodeRegenerating}
                    >
                        {categoryBarcodeRegenerating ? 'Generando...' : 'Generar categorias'}
                    </button>
                </div>

                {categoryBarcodeError && (
                    <p className="text-xs text-red-600">{categoryBarcodeError}</p>
                )}
                {categoryBarcodeSaved && !categoryBarcodeError && (
                    <p className="text-xs text-green-600">Barcodes de categorias generados.</p>
                )}
                {categoryBarcodeInactiveCount > 0 && (
                    <p className="text-xs text-gray-500">
                        {categoryBarcodeInactiveCount} categorias inactivas se conservan en el catalogo.
                    </p>
                )}

                {categoryBarcodeLoading ? (
                    <InlineLoading label="Cargando categorias" />
                ) : categoryBarcodes.length ? (
                    <div className="max-w-full overflow-x-auto border border-gray-200 rounded bg-white">
                        <table className="min-w-[640px] w-full text-sm">
                            <thead className="bg-gray-100 text-xs text-gray-600 uppercase">
                                <tr>
                                    <th className="px-3 py-2 text-left">Categoria</th>
                                    <th className="px-3 py-2 text-left">ID</th>
                                    <th className="px-3 py-2 text-left">Identificador</th>
                                    <th className="px-3 py-2 text-left">Actualizado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {categoryBarcodes.map((entry) => (
                                    <tr key={entry.id || entry.categoryId}>
                                        <td className="px-3 py-2 text-gray-800">
                                            {entry.categoryName || `Categoria ${entry.categoryId}`}
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">
                                            {entry.categoryId}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-gray-900">
                                            {entry.barcode || (
                                                <span className="font-sans text-xs text-red-600">
                                                    categoria excede limite
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">
                                            {formatCategoryBarcodeDate(entry.lastGeneratedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="border border-dashed border-gray-300 rounded p-4 text-center text-xs text-gray-500 bg-white">
                        No hay barcodes de categorias generados.
                    </div>
                )}
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-3">{error}</p>
            )}
            {saved && (
                <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
            )}

            <div className="flex items-center gap-3 mt-4">
                <button
                    onClick={handleSave}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                    Guardar
                </button>
                <button
                    onClick={handleReset}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
                >
                    Restablecer
                </button>
            </div>
        </div>
    );

    const renderPrintRedirectDefaults = () => {
        if (!canManageGlobalSettings) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para editar la configuracion global de impresion.
                </div>
            );
        }

        return (
            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Redireccion por defecto</h2>
                    <p className="text-xs text-gray-500">
                        Define el tiempo usado por usuarios sin una redireccion personalizada.
                    </p>
                </div>
                {printRedirectDefaultsLoading ? (
                    <InlineLoading label="Cargando configuracion" />
                ) : (
                    <div className="max-w-xs">
                        <label htmlFor="print-redirect-default-delay" className="block text-xs font-semibold text-gray-600 mb-1">
                            Tiempo de espera (segundos)
                        </label>
                        <input
                            id="print-redirect-default-delay"
                            type="number"
                            min="1"
                            max="300"
                            value={printRedirectDefaults.delaySeconds ?? ''}
                            onChange={(e) => handlePrintRedirectDefaultDelayChange(e.target.value)}
                            disabled={printRedirectDefaultsSaving}
                            className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                        />
                    </div>
                )}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSavePrintRedirectDefaults}
                        disabled={printRedirectDefaultsLoading || printRedirectDefaultsSaving}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                    >
                        {printRedirectDefaultsSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                        onClick={handleResetPrintRedirectDefaults}
                        disabled={printRedirectDefaultsLoading || printRedirectDefaultsSaving}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                    >
                        Restablecer
                    </button>
                </div>
                {printRedirectDefaultsError && (
                    <p className="text-sm text-red-600">{printRedirectDefaultsError}</p>
                )}
                {printRedirectDefaultsSaved && (
                    <p className="text-sm text-green-600">Configuracion guardada.</p>
                )}
                <p className="text-xs text-gray-500">
                    Cada usuario puede reemplazar este tiempo desde sus preferencias personales de impresion.
                </p>
            </div>
        );
    };

    const renderPrintSettings = () => (
        <div className="border rounded-lg p-4 space-y-4">
            <div>
                <h2 className="text-md font-semibold">Impresion de comprobantes</h2>
                <p className="text-xs text-gray-500">
                    Define como imprimir desde este navegador.
                </p>
            </div>
            <div className="space-y-3">
                {PRINT_MODE_OPTIONS.map((mode) => (
                    <label
                        key={mode}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                            printMode === mode
                                ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <input
                            type="radio"
                            name="print-mode"
                            value={mode}
                            checked={printMode === mode}
                            onChange={(e) => handlePrintModeChange(e.target.value)}
                            className="mt-1"
                        />
                        <div>
                            <div className="text-sm font-medium">
                                {PRINT_MODE_LABELS[mode] || mode}
                            </div>
                            {PRINT_MODE_DESCRIPTIONS[mode] && (
                                <div className="text-xs text-gray-500">
                                    {PRINT_MODE_DESCRIPTIONS[mode]}
                                </div>
                            )}
                        </div>
                    </label>
                ))}
            </div>
            <div className="border-t pt-4 space-y-3">
                <div>
                    <h3 className="text-sm font-semibold">Redireccion despues de imprimir</h3>
                    <p className="text-xs text-gray-500">
                        Aplica solo al boton Imprimir Recibo en la pagina de comprobante.
                    </p>
                </div>
                <label className="flex items-center gap-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(printReceiptRedirect.enabled)}
                        onChange={(e) => handlePrintRedirectEnabledChange(e.target.checked)}
                        disabled={preferencesLoading || printSettingsSaving}
                    />
                    <span>Redirigir automaticamente a nueva venta</span>
                </label>
                <div className="max-w-xs">
                    <label htmlFor="print-redirect-delay" className="block text-xs font-semibold text-gray-600 mb-1">
                        Tiempo de espera (segundos)
                    </label>
                    <input
                        id="print-redirect-delay"
                        type="number"
                        min="1"
                        max="300"
                        value={printReceiptRedirect.delaySeconds ?? ''}
                        onChange={(e) => handlePrintRedirectDelayChange(e.target.value)}
                        disabled={!printReceiptRedirect.enabled || preferencesLoading || printSettingsSaving}
                        className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    />
                </div>
                {!preferencesLoading && (
                    <p className="text-xs text-gray-500">
                        {myPreferences?.hasCustomPrintReceiptRedirect
                            ? 'Este usuario tiene una redireccion personalizada.'
                            : `Usando el tiempo por defecto global: ${printReceiptRedirect.delaySeconds || DEFAULT_PRINT_RECEIPT_REDIRECT_DEFAULTS.delaySeconds} segundos.`}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleSavePrintMode}
                    disabled={preferencesLoading || printSettingsSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                >
                    {printSettingsSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                    onClick={handleResetPrintMode}
                    disabled={preferencesLoading || printSettingsSaving}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                >
                    Restablecer
                </button>
            </div>
            {(preferencesError || printSettingsError) && (
                <p className="text-sm text-red-600">{printSettingsError || preferencesError}</p>
            )}
            {printModeSaved && (
                <p className="text-sm text-green-600">Configuracion guardada.</p>
            )}
            <p className="text-xs text-gray-500">
                El modo de impresion se guarda en este navegador. La redireccion se guarda para tu usuario; restablecer vuelve al predeterminado global.
            </p>
        </div>
    );

    const renderPurchaseOrderReviewSettings = () => (
        <div className="border rounded-lg p-4 space-y-4">
            <div>
                <h2 className="text-md font-semibold">Revision de ordenes de compra</h2>
                <p className="text-xs text-gray-500">
                    Define como se guardan y confirman los items cuando revisas ordenes.
                </p>
            </div>
            <div className="space-y-3">
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(purchaseOrderReview.autoSaveItemChanges)}
                        onChange={(event) => handlePurchaseOrderReviewChange('autoSaveItemChanges', event.target.checked)}
                        disabled={preferencesLoading || purchaseOrderReviewSaving}
                        className="mt-1"
                    />
                    <span>
                        <span className="block font-medium text-gray-900">Guardar cambios automaticamente</span>
                        <span className="block text-xs text-gray-500">
                            Cantidades y motivos se guardan al editar. Si lo desactivas, deberas guardar cada item.
                        </span>
                    </span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(purchaseOrderReview.requireItemConfirmation)}
                        onChange={(event) => handlePurchaseOrderReviewChange('requireItemConfirmation', event.target.checked)}
                        disabled={preferencesLoading || purchaseOrderReviewSaving}
                        className="mt-1"
                    />
                    <span>
                        <span className="block font-medium text-gray-900">Confirmar items procesados por separado</span>
                        <span className="block text-xs text-gray-500">
                            Los cambios guardados quedan pendientes hasta confirmar que el item fue procesado.
                        </span>
                    </span>
                </label>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleSavePurchaseOrderReview}
                    disabled={preferencesLoading || purchaseOrderReviewSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                >
                    {purchaseOrderReviewSaving ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                    onClick={handleResetPurchaseOrderReview}
                    disabled={preferencesLoading || purchaseOrderReviewSaving}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300 disabled:opacity-60"
                >
                    Restablecer
                </button>
            </div>
            {(preferencesError || purchaseOrderReviewError) && (
                <p className="text-sm text-red-600">{purchaseOrderReviewError || preferencesError}</p>
            )}
            {purchaseOrderReviewSaved && (
                <p className="text-sm text-green-600">Configuracion guardada.</p>
            )}
            <p className="text-xs text-gray-500">
                Esta configuracion se guarda para tu usuario y aplica en cualquier navegador.
            </p>
        </div>
    );

    const renderTaxSettings = () => (
        <div className="space-y-6">
            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Impuestos</h2>
                    <p className="text-xs text-gray-500">Administra el IVA y otros impuestos.</p>
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos.</p>
                )}
                {canReadTaxes && taxesError && (
                    <p className="text-sm text-red-600">{taxesError}</p>
                )}
                {canReadTaxes && taxesLoading && (
                    <InlineLoading label="Cargando impuestos" />
                )}

                {canReadTaxes && !taxesLoading && (
                    <>
                        {canManageTaxes && (
                            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                                <div className="text-sm font-semibold text-gray-700">Crear impuesto</div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                        <input
                                            type="text"
                                            value={taxForm.name}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, name: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                        <input
                                            type="number"
                                            value={taxForm.rate}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, rate: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.applyToAllProducts}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, applyToAllProducts: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Aplicar a todos
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.isActive}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Activo
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                        onClick={handleCreateTax}
                                        disabled={taxSaving}
                                    >
                                        {taxSaving ? 'Guardando...' : 'Agregar impuesto'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                            <div className="text-sm font-semibold text-gray-700">Impuestos registrados</div>
                            {taxes.length === 0 && (
                                <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                            )}
                            {taxes.map((tax) => {
                                const draft = taxEdits[tax.id] || {};
                                return (
                                    <div key={tax.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                                <input
                                                    type="text"
                                                    value={draft.name ?? tax.name}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'name', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                                <input
                                                    type="number"
                                                    value={draft.rate ?? tax.rate}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'rate', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.applyToAllProducts ?? tax.applyToAllProducts}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'applyToAllProducts', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Aplicar a todos
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.isActive ?? tax.isActive}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'isActive', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Activo
                                                </label>
                                            </div>
                                        </div>
                                        {canManageTaxes && (
                                            <div>
                                                <button
                                                    type="button"
                                                    className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                                                    onClick={() => handleSaveTax(tax.id)}
                                                    disabled={taxSaving}
                                                >
                                                    Guardar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Activacion por sucursal</h2>
                    <p className="text-xs text-gray-500">
                        Define que impuestos aplican en cada sucursal.
                    </p>
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos por sucursal.</p>
                )}
                {canReadTaxes && branchTaxError && (
                    <p className="text-sm text-red-600">{branchTaxError}</p>
                )}
                {canReadTaxes && branchTaxLoading && (
                    <InlineLoading label="Cargando impuestos por sucursal" />
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length === 0 && (
                    <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length > 0 && (
                    <div className="max-w-full overflow-x-auto">
                        <table className="min-w-[720px] w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Sucursal</th>
                                    {taxes.map((tax) => (
                                        <th
                                            key={tax.id}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase"
                                        >
                                            {tax.name}
                                        </th>
                                    ))}
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {activeBranches.map((branch) => {
                                    const branchMap = branchTaxMap[branch.id] || {};
                                    const isSaving = !!branchTaxSaving[branch.id];
                                    const isDirty = !!branchTaxDirty[branch.id];
                                    return (
                                        <tr key={branch.id}>
                                            <td className="px-3 py-2 font-medium text-gray-800">
                                                {branch.branchName || `Sucursal ${branch.id}`}
                                            </td>
                                            {taxes.map((tax) => (
                                                <td key={tax.id} className="px-3 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!branchMap[tax.id]}
                                                        onChange={() => handleBranchTaxToggle(branch.id, tax.id)}
                                                        disabled={!canManageTaxes || tax.isActive === false || isSaving}
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-3 py-2 text-right">
                                                {canManageTaxes && (
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-60"
                                                        onClick={() => handleSaveBranchTaxes(branch.id)}
                                                        disabled={!isDirty || isSaving}
                                                    >
                                                        {isSaving ? 'Guardando...' : 'Guardar'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {activeBranches.length === 0 && (
                                    <tr>
                                        <td colSpan={taxes.length + 2} className="px-3 py-4 text-center text-xs text-gray-500">
                                            No hay sucursales registradas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderClientPricingCategories = () => {
        const selectedCategory = clientPricingCategories.find(
            (category) => String(category.id) === String(selectedClientPricingCategoryId)
        );
        if (!canManageClientPricing) {
            return (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    No tienes permisos para administrar categorias de descuento.
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {(clientPricingCategoriesError || clientPricingDiscountsError) && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {clientPricingCategoriesError || clientPricingDiscountsError}
                    </div>
                )}
                {clientPricingDiscountsSaved && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                        Descuentos actualizados.
                    </div>
                )}

                <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                        <div className="text-xs font-semibold uppercase text-blue-700">1. Categorias</div>
                        <p className="mt-1 text-sm text-gray-700">
                            Define grupos globales de clientes.
                        </p>
                    </div>
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-3">
                        <div className="text-xs font-semibold uppercase text-emerald-700">2. Clientes</div>
                        <p className="mt-1 text-sm text-gray-700">
                            Asigna una categoria en el formulario del cliente.
                        </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="text-xs font-semibold uppercase text-slate-700">3. Mayorista</div>
                        <p className="mt-1 text-sm text-gray-700">
                            Configura descuentos solo sobre precios mayorista.
                        </p>
                    </div>
                </div>

                <div className="grid min-w-0 gap-4 2xl:grid-cols-[minmax(260px,320px),minmax(0,1fr)]">
                    <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Categorias de descuento</h3>
                                <p className="text-xs text-gray-500">Un cliente puede tener una categoria activa.</p>
                            </div>
                            <div className="text-right">
                                {clientPricingCategoriesLoading ? (
                                    <InlineLoading label="Cargando" />
                                ) : (
                                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
                                        {clientPricingCategories.length} categorias
                                    </span>
                                )}
                            </div>
                        </div>

                        <form onSubmit={handleSubmitClientPricingCategory} className="mt-4 space-y-2">
                            <input
                                type="text"
                                value={clientPricingCategoryForm.name}
                                onChange={(event) => setClientPricingCategoryForm((prev) => ({ ...prev, name: event.target.value }))}
                                placeholder="Nombre de categoria"
                                className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                            />
                            <textarea
                                value={clientPricingCategoryForm.description}
                                onChange={(event) => setClientPricingCategoryForm((prev) => ({ ...prev, description: event.target.value }))}
                                placeholder="Descripcion opcional"
                                rows={2}
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            />
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={clientPricingCategoryForm.isActive}
                                    onChange={(event) => setClientPricingCategoryForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                                    className="h-4 w-4 accent-blue-600"
                                />
                                Activa
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="submit"
                                    disabled={clientPricingCategoriesLoading}
                                    className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {clientPricingEditingId ? 'Guardar categoria' : 'Crear categoria'}
                                </button>
                                {clientPricingEditingId && (
                                    <button
                                        type="button"
                                        onClick={resetClientPricingCategoryForm}
                                        className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </form>

                        <div className="mt-4 space-y-2">
                            {clientPricingCategories.map((category) => {
                                const active = String(category.id) === String(selectedClientPricingCategoryId);
                                return (
                                    <div
                                        key={category.id}
                                        className={`rounded-lg border px-3 py-2 ${active ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setSelectedClientPricingCategoryId(String(category.id))}
                                            className="w-full text-left"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold text-gray-900">{category.name}</span>
                                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                    category.isActive === false
                                                        ? 'bg-gray-100 text-gray-500'
                                                        : 'bg-green-100 text-green-700'
                                                }`}
                                                >
                                                    {category.isActive === false ? 'Inactiva' : 'Activa'}
                                                </span>
                                            </div>
                                            {category.description && (
                                                <p className="mt-1 text-xs text-gray-500">{category.description}</p>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleEditClientPricingCategory(category)}
                                            className="mt-2 text-xs font-semibold text-blue-700 hover:underline"
                                        >
                                            Editar
                                        </button>
                                    </div>
                                );
                            })}
                            {!clientPricingCategoriesLoading && clientPricingCategories.length === 0 && (
                                <p className="rounded border border-dashed border-gray-300 px-3 py-4 text-sm text-gray-500">
                                    Crea una categoria para configurar descuentos.
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    {selectedCategory ? `Descuentos · ${selectedCategory.name}` : 'Descuentos mayorista'}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Solo se muestran precios mayorista. La sucursal seleccionada se usa como referencia de precio y cada fila se guarda automaticamente.
                                </p>
                            </div>
                            {clientPricingDiscountsLoading && <InlineLoading label="Cargando precios" />}
                        </div>

                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <div className="text-sm font-semibold text-gray-800">Herramientas masivas</div>
                                    <p className="text-xs text-gray-500">
                                        Aplica o limpia descuentos en las filas mayorista visibles.
                                    </p>
                                </div>
                                {selectedCategory && (
                                    <span className="w-fit rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                                        {selectedCategory.name}
                                    </span>
                                )}
                            </div>
                            <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(0,1fr),auto]">
                            <input
                                type="search"
                                value={clientPricingSearch}
                                onChange={(event) => setClientPricingSearch(event.target.value)}
                                placeholder="Buscar producto, variedad o formato"
                                className="h-10 w-full min-w-0 rounded border border-gray-300 px-3 text-sm"
                                disabled={!selectedClientPricingCategoryId}
                            />
                            <div className="flex min-w-0 flex-wrap gap-2">
                                <select
                                    value={clientPricingBulkForm.discountType}
                                    onChange={(event) => setClientPricingBulkForm((prev) => ({ ...prev, discountType: event.target.value }))}
                                    className="h-10 rounded border border-gray-300 px-2 text-sm"
                                    disabled={!selectedClientPricingCategoryId}
                                >
                                    <option value="amount">Monto</option>
                                    <option value="percent">%</option>
                                </select>
                                <input
                                    type="number"
                                    min="0"
                                    value={clientPricingBulkForm.discountValue}
                                    onChange={(event) => setClientPricingBulkForm((prev) => ({ ...prev, discountValue: event.target.value }))}
                                    placeholder={clientPricingBulkForm.discountType === 'percent' ? '10' : '2000'}
                                    className="h-10 w-24 rounded border border-gray-300 px-2 text-sm"
                                    disabled={!selectedClientPricingCategoryId}
                                />
                                <button
                                    type="button"
                                    onClick={() => handleBulkClientPricingDiscounts('apply')}
                                    disabled={!selectedClientPricingCategoryId || clientPricingBulkSaving}
                                    className="rounded bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
                                >
                                    Aplicar visibles
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleBulkClientPricingDiscounts('clear')}
                                    disabled={!selectedClientPricingCategoryId || clientPricingBulkSaving}
                                    className="rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                                >
                                    Limpiar visibles
                                </button>
                            </div>
                            </div>
                        </div>

                        <div className="mt-4 max-w-full overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-[780px] w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Producto</th>
                                        <th className="px-3 py-2 text-left">Formato</th>
                                        <th className="px-3 py-2 text-right">Base</th>
                                        <th className="px-3 py-2 text-left">Descuento</th>
                                        <th className="px-3 py-2 text-right">Final</th>
                                        <th className="px-3 py-2 text-right">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {clientPricingDiscountRows.map((row) => {
                                        const rowSaving = !!clientPricingRowSaving[row.varietyPriceId];
                                        const rowStatus = clientPricingRowSaveStatus[row.varietyPriceId] || 'idle';
                                        const statusLabel = rowSaving || rowStatus === 'saving'
                                            ? 'Guardando...'
                                            : rowStatus === 'saved'
                                                ? 'Guardado'
                                                : rowStatus === 'error'
                                                    ? 'Error'
                                                    : rowStatus === 'pending'
                                                        ? 'Pendiente'
                                                        : 'Auto';
                                        const statusClass = rowSaving || rowStatus === 'saving' || rowStatus === 'pending'
                                            ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                                            : rowStatus === 'saved'
                                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                                                : rowStatus === 'error'
                                                    ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                                                    : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200';
                                        const basePrice = Number(row.basePrice || 0);
                                        const draftValue = Number(row.discountValue || 0);
                                        const discountAmount = Math.min(
                                            Math.max(
                                                row.discountType === 'percent'
                                                    ? Math.floor(basePrice * (draftValue / 100))
                                                    : Math.floor(draftValue),
                                                0
                                            ),
                                            basePrice
                                        );
                                        const finalPrice = Math.max(basePrice - discountAmount, 0);
                                        return (
                                            <tr key={row.varietyPriceId}>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-gray-900 break-words">{row.productName}</div>
                                                    <div className="text-xs text-gray-500 break-words">{row.varietyName}</div>
                                                </td>
                                                <td className="px-3 py-2 text-gray-700">
                                                    {[row.metricUnit, row.description].filter(Boolean).join(' · ') || 'Mayorista'}
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium text-gray-900">
                                                    {formatMoney(basePrice)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex min-w-[190px] items-center gap-2">
                                                        <select
                                                            value={row.discountType || 'amount'}
                                                            onChange={(event) => handleClientPricingRowDraftChange(row.varietyPriceId, { discountType: event.target.value })}
                                                            className="h-9 rounded border border-gray-300 px-2 text-sm"
                                                        >
                                                            <option value="amount">Monto</option>
                                                            <option value="percent">%</option>
                                                        </select>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={row.discountValue ?? ''}
                                                            onChange={(event) => handleClientPricingRowDraftChange(row.varietyPriceId, { discountValue: event.target.value })}
                                                            className="h-9 w-24 rounded border border-gray-300 px-2 text-sm"
                                                        />
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        {discountAmount > 0 ? `-${formatMoney(discountAmount)}` : 'Sin descuento'}
                                                    </p>
                                                </td>
                                                <td className="px-3 py-2 text-right font-semibold text-green-700">
                                                    {formatMoney(finalPrice)}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                                                        {statusLabel}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!clientPricingDiscountsLoading && clientPricingDiscountRows.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                                                {selectedClientPricingCategoryId
                                                    ? 'No hay precios mayorista visibles para este filtro.'
                                                    : 'Selecciona o crea una categoria.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
        );
    };

    const renderSectionContent = () => {
        switch (activeSectionConfig?.id) {
            case 'users-access':
                return renderUsersAccess();
            case 'branch-directory':
                return renderBranchDirectory();
            case 'branch-price-sync':
                return renderPriceSync();
            case 'purchase-order-reasons':
                return renderPurchaseOrderReasons();
            case 'purchase-order-notifications':
                return renderPurchaseOrderNotifications();
            case 'purchase-order-review':
                return renderPurchaseOrderReviewSettings();
            case 'client-pricing-categories':
                return renderClientPricingCategories();
            case 'registers-cash':
                return renderRegisters();
            case 'registers-print-defaults':
                return renderPrintRedirectDefaults();
            case 'registers-barcode':
                return renderBarcodes();
            case 'registers-printing':
                return renderPrintSettings();
            case 'registers-pos-favs':
                return renderPosFavs();
            case 'taxes-manage':
                return renderTaxSettings();
            case 'dte-company':
            case 'dte-credentials':
            case 'dte-branches':
            case 'dte-cafs':
            case 'dte-automation':
                return (
                    <DteSettingsPanel
                        sectionId={activeSectionConfig.id}
                        branches={activeBranches}
                        canManage={canManageDte}
                    />
                );
            case 'catalog-products':
                return renderCatalogProducts();
            case 'catalog-categories':
                return renderCatalogCategories();
            case 'catalog-promotions':
                return renderCatalogPromotions();
            case 'catalog-pos-sales':
                return renderCatalogPosSales();
            case 'catalog-price-recommendations':
                return renderCatalogPriceRecommendations();
            case 'catalog-price-list':
                return (
                    <AdminPriceListPage
                        embedded
                        onOpenFullPage={() =>
                            navigate(
                                activeBranchId
                                    ? `/branches/${activeBranchId}/price-list`
                                    : '/branches'
                            )
                        }
                    />
                );
            default:
                return null;
        }
    };

    const showSidebarHeader = !isSidebarCollapsed;
    const sidebarHeaderClass = isSidebarCollapsed ? 'hidden xl:group-hover:block' : 'block';
    const sidebarLabelClass = isSidebarCollapsed
        ? 'xl:hidden xl:group-hover:block'
        : 'block';
    const sidebarDescriptionClass = isSidebarCollapsed
        ? 'hidden xl:group-hover:block'
        : 'hidden xl:block';
    const sidebarSectionClass = isSidebarCollapsed
        ? 'hidden xl:group-hover:block'
        : 'block';

    return (
        <div className="min-h-[90vh] overflow-x-hidden bg-gray-100 pt-16 px-2 pb-6 sm:px-4 lg:px-6">
            <div className="mx-auto w-full max-w-screen-2xl">
                <div className="min-w-0 bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b px-5 py-3">
                        <h1 className="text-xl font-bold">Configuracion</h1>
                        <p className="text-sm text-gray-600">
                            Ajusta tus preferencias y las configuraciones disponibles para tu rol.
                        </p>
                    </div>
                    <div className="flex min-w-0 flex-col xl:flex-row">
                        <aside
                            className={`group w-full xl:flex-none border-b xl:border-b-0 xl:border-r p-3 bg-white overflow-hidden transition-[width,max-height] ${
                                isSidebarCollapsed ? 'max-h-28 xl:w-16 xl:hover:w-72' : 'max-h-[720px] xl:w-72'
                            } xl:max-h-none`}
                        >
                            <div
                                className={`flex items-center mb-3 ${
                                    showSidebarHeader ? 'justify-between' : 'justify-center'
                                }`}
                            >
                                <div className={`text-xs uppercase text-gray-400 ${sidebarHeaderClass}`}>
                                    Configuracion
                                </div>
                                <div className="flex items-center gap-2">
                                    {!isSidebarCollapsed && (
                                        <button
                                            type="button"
                                            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                            className="xl:hidden text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
                                            aria-label="Ocultar opciones"
                                        >
                                            <FiChevronUp />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                        className="hidden xl:inline-flex text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
                                        aria-label={isSidebarCollapsed ? 'Expandir' : 'Contraer'}
                                    >
                                        <TiPin className={isSidebarCollapsed ? '-rotate-45' : ''} />
                                    </button>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="flex flex-col gap-2 overflow-y-auto pr-1">
                                    {visibleTopics.map((topic) => {
                                        const isActive = topic.id === activeTopicConfig?.id;
                                        return (
                                            <div key={topic.id} className="flex flex-col">
                                                <button
                                                    onClick={() => handleTopicSelect(topic.id)}
                                                    className={`w-full text-left px-3 py-2 rounded-lg border ${
                                                        isActive
                                                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                                                            : 'border-transparent hover:border-gray-200 hover:bg-gray-50 text-gray-600'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-base">{topicIcons[topic.id] || <FiSettings />}</span>
                                                        <div className={sidebarLabelClass}>
                                                            <div className="text-sm font-semibold">{topic.label}</div>
                                                            <div className={`text-xs text-gray-500 ${sidebarDescriptionClass}`}>
                                                                {topic.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                                <div
                                                    className={`mt-1 ml-6 rounded-lg border border-gray-200 bg-gray-100 p-1.5 flex flex-col gap-1 ${
                                                        isActive ? sidebarSectionClass : 'hidden'
                                                    }`}
                                                >
                                                    {topic.sections.map((section) => {
                                                        const isSectionActive = section.id === activeSectionConfig?.id;
                                                        return (
                                                            <button
                                                                key={section.id}
                                                                onClick={() => handleSectionSelect(section.id)}
                                                                className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                                                    isSectionActive
                                                                        ? 'border-blue-200 bg-white text-blue-700'
                                                                        : 'border-transparent hover:border-gray-200 text-gray-600'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm">
                                                                        {sectionIcons[section.id] || <FiSettings />}
                                                                    </span>
                                                                    <div className={sidebarLabelClass}>
                                                                        <div className="font-medium">{section.label}</div>
                                                                        <div className={`text-xs text-gray-500 ${sidebarDescriptionClass}`}>
                                                                            {section.description}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {isSidebarCollapsed && (
                                    <button
                                        type="button"
                                        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                        className="xl:hidden absolute right-2 top-1/2 -translate-y-1/2 px-2 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm"
                                        aria-label="Mostrar opciones"
                                    >
                                        <FiChevronDown />
                                    </button>
                                )}
                            </div>
                        </aside>
                        <div className="min-w-0 flex-1 p-3 sm:p-4 lg:p-5">
                            <div className="min-w-0 flex-1 space-y-6">
                                {renderBranchTabs()}
                                {renderSectionContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {branchCreateOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div
                        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500">Sucursales</p>
                                <h2 className="text-lg font-semibold text-gray-900">Nueva sucursal</h2>
                                <p className="text-xs text-gray-500 mt-1">
                                    Crea la sucursal y configura al menos una caja.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCloseCreateBranch}
                                className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                                disabled={branchCreating}
                            >
                                x
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">
                                    Nombre de sucursal
                                </label>
                                <input
                                    type="text"
                                    value={newBranchName}
                                    onChange={(e) => setNewBranchName(e.target.value)}
                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                    placeholder="Sucursal principal"
                                    disabled={branchCreating}
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">
                                        Cajas iniciales
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAddRegister}
                                        className="text-xs text-blue-600 hover:text-blue-700"
                                        disabled={branchCreating}
                                    >
                                        + Agregar caja
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {newBranchRegisters.map((register, index) => {
                                        const registerLabel = register?.name?.trim() || `Caja ${index + 1}`;
                                        const allowedTypes = Array.isArray(register?.allowedClientTypes)
                                            ? register.allowedClientTypes
                                            : [];
                                        const selectedUsers = Array.isArray(register?.users) ? register.users : [];
                                        const showUsersGrid =
                                            canReadUsers
                                            && !branchCreateUsersLoading
                                            && !branchCreateUsersError
                                            && branchCreateAvailableUsers.length > 0;
                                        return (
                                            <div key={`register-${index}`} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-sm font-semibold text-gray-800">{registerLabel}</h4>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveRegister(index)}
                                                        disabled={branchCreating || newBranchRegisters.length === 1}
                                                        className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                                    >
                                                        Quitar
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                                        <input
                                                            type="text"
                                                            value={register?.name || ''}
                                                            onChange={(e) => handleRegisterNameChange(index, e.target.value)}
                                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                            placeholder={`Caja ${index + 1}`}
                                                            disabled={branchCreating}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 uppercase">Limite de ventas abiertas</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={register?.maxOpenSales ?? '5'}
                                                            onChange={(e) => handleRegisterMaxOpenSalesChange(index, e.target.value)}
                                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                            disabled={branchCreating}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={register?.isActive ?? true}
                                                                onChange={(e) => handleRegisterActiveChange(index, e.target.checked)}
                                                                disabled={branchCreating}
                                                            />
                                                            Caja activa
                                                        </label>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Tipos de cliente permitidos
                                                    </label>
                                                    <div className="flex flex-wrap gap-3">
                                                        {Object.entries(CLIENT_TYPE_LABELS).map(([type, label]) => (
                                                            <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allowedTypes.includes(type)}
                                                                    onChange={() => toggleRegisterClientType(index, type)}
                                                                    disabled={branchCreating}
                                                                />
                                                                {label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Encargado de autorizacion de descuentos
                                                    </label>
                                                    <select
                                                        className="w-full border border-gray-300 rounded-lg p-2"
                                                        value={register?.discountApproverUserId || ''}
                                                        onChange={(e) => handleRegisterDiscountApproverChange(index, e.target.value)}
                                                        disabled={branchCreating || !canReadUsers || branchCreateUsersLoading || Boolean(branchCreateUsersError)}
                                                    >
                                                        <option value="">Sin encargado</option>
                                                        {branchCreateAvailableUsers.map((user) => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.name || user.username}
                                                                {hasPermission(user, 'branch.access.any') ? ' (Admin)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {!canReadUsers && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            No tienes permisos para cargar usuarios.
                                                        </p>
                                                    )}
                                                    {canReadUsers && branchCreateUsersLoading && (
                                                        <div className="mt-2">
                                                            <InlineLoading label="Cargando usuarios" />
                                                        </div>
                                                    )}
                                                    {canReadUsers && !branchCreateUsersLoading && branchCreateUsersError && (
                                                        <p className="text-xs text-red-600 mt-1">{branchCreateUsersError}</p>
                                                    )}
                                                    {canReadUsers
                                                        && !branchCreateUsersLoading
                                                        && !branchCreateUsersError
                                                        && branchCreateAvailableUsers.length === 0 && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                No hay usuarios administradores disponibles.
                                                            </p>
                                                        )}
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Usuarios con acceso
                                                    </label>
                                                    {!canReadUsers && (
                                                        <p className="text-xs text-gray-500">
                                                            No tienes permisos para cargar usuarios.
                                                        </p>
                                                    )}
                                                    {canReadUsers && branchCreateUsersLoading && (
                                                        <InlineLoading label="Cargando usuarios" />
                                                    )}
                                                    {canReadUsers && !branchCreateUsersLoading && branchCreateUsersError && (
                                                        <p className="text-xs text-red-600">{branchCreateUsersError}</p>
                                                    )}
                                                    {showUsersGrid && (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                                            {branchCreateAvailableUsers.map((user) => (
                                                                <label key={user.id} className="flex items-center gap-2 text-sm text-gray-700">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedUsers.includes(String(user.id))}
                                                                        onChange={() => toggleRegisterUser(index, String(user.id))}
                                                                        disabled={branchCreating}
                                                                    />
                                                                    <span>{user.name || user.username}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {canReadUsers
                                                        && !branchCreateUsersLoading
                                                        && !branchCreateUsersError
                                                        && branchCreateAvailableUsers.length === 0 && (
                                                            <p className="text-xs text-gray-500">
                                                                Asigna usuarios desde "Usuarios" o crea administradores.
                                                            </p>
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Se requiere al menos una caja.
                                </p>
                            </div>

                            <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-800">Impuestos para la sucursal</h4>
                                    <p className="text-xs text-gray-500">
                                        Selecciona los impuestos que estaran activos en esta sucursal.
                                    </p>
                                </div>
                                {!canReadTaxes && (
                                    <p className="text-xs text-gray-500">No tienes permisos para ver impuestos.</p>
                                )}
                                {canReadTaxes && taxesLoading && (
                                    <InlineLoading label="Cargando impuestos" />
                                )}
                                {canReadTaxes && !taxesLoading && taxesError && (
                                    <p className="text-xs text-red-600">{taxesError}</p>
                                )}
                                {canReadTaxes && !taxesLoading && !taxesError && taxes.length === 0 && (
                                    <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                                )}
                                {canReadTaxes && !taxesLoading && taxes.length > 0 && (
                                    <div className="flex flex-wrap gap-3">
                                        {taxes.map((tax) => (
                                            <label
                                                key={tax.id}
                                                className={`flex items-center gap-2 text-sm ${
                                                    tax.isActive === false ? 'text-gray-400' : 'text-gray-700'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!newBranchTaxSelection[tax.id]}
                                                    onChange={(e) =>
                                                        setNewBranchTaxSelection((prev) => ({
                                                            ...prev,
                                                            [tax.id]: e.target.checked,
                                                        }))
                                                    }
                                                    disabled={branchCreating || tax.isActive === false}
                                                />
                                                {tax.name} ({Number(tax.rate || 0)}%)
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {branchCreateError && (
                                <p className="text-sm text-red-600">{branchCreateError}</p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900"
                                onClick={handleCloseCreateBranch}
                                disabled={branchCreating}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                onClick={handleCreateBranch}
                                disabled={branchCreating}
                            >
                                {branchCreating ? 'Creando...' : 'Crear sucursal'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;
